import React, { useMemo, useState } from 'react';
import { Box, Grid, Typography, Paper, Divider, Card, CardContent, Avatar, Chip, Button, Tooltip, LinearProgress, TextField, InputAdornment, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Stack, Badge, useTheme, useMediaQuery, Snackbar, Alert, CircularProgress, Menu, MenuItem, FormControl, InputLabel, Select, FormGroup, FormControlLabel, Checkbox, ListItemIcon, ListItemText, ButtonGroup } from '@mui/material';
import { 
  AssignmentTurnedIn, Error, Pending, CheckCircle, 
  AccessTime, PriorityHigh, Person, Visibility, Group,
  FilterListOff, Search, Clear, Close, InfoOutlined,
  CheckCircleOutline, ErrorOutline, HourglassEmpty, Send, Info,
  Download, FilterList, Warning, CompareArrows, GridOn as GridView, ViewList
} from '@mui/icons-material';
import DonutChart from '../../common/DonutChart';
import { DataGrid } from '@mui/x-data-grid';
import { PieChart } from '@mui/x-charts/PieChart';
import { alpha, styled } from '@mui/material/styles';
import uarService from '../../../services/UARService';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadius * 2,
  //backgroundColor: theme.palette.mode === 'dark' ? '#1A2027' : '#fff',
  //border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  //boxShadow: '0 4px 12px rgba(0,0,0,0.04)',
  transition: theme.transitions.create(['box-shadow', 'transform'], {
    duration: theme.transitions.duration.enteringScreen,
  }),
  '&:hover': {
    //transform: 'translateY(-2px)',
    //boxShadow: '0 8px 20px rgba(0,0,0,0.06)',
  },
}));

const UARReviewDashboard = ({ userswithSOD, rolesData, appRecord, roleOwnerList }) => {
  // Change from single role filter to array of selected roles
  const [selectedRoleFilter, setSelectedRoleFilter] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [openUserDialog, setOpenUserDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    onConfirm: null
  });
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  // Add state for role menu
  const [roleMenuAnchorEl, setRoleMenuAnchorEl] = useState(null);
  // Update view mode to use localStorage for persistence
  const [roleOwnersViewMode, setRoleOwnersViewMode] = useState(() => {
    // Try to get saved preference from localStorage
    const savedViewMode = localStorage.getItem('roleOwnersViewMode');
    // Return saved value if it exists, otherwise default to 'card'
    return savedViewMode === 'list' ? 'list' : 'card';
  });
  
  // Add new state for status filters
  const [statusFilters, setStatusFilters] = useState({
    approved: true,
    rejected: true,
    pending: true
  });
  const [filterMenuAnchorEl, setFilterMenuAnchorEl] = useState(null);
  const [openMonitoringDialog, setOpenMonitoringDialog] = useState(false);

  // Process data for the dashboard
  const dashboardData = useMemo(() => {
    // Get unique roles and their review status
    const roles = {};
    const users = {};
    const roleOwners = {};

    // Create a direct mapping of UUID → owner name from roleOwnerList
    const roleOwnerIdToNameMap = {};
    
    if (roleOwnerList && roleOwnerList.length) {
      roleOwnerList.forEach(owner => {
        if (owner.value) {
          roleOwnerIdToNameMap[owner.value] = owner.label;
        }
      });
    }
    
    // Debug UUIDs that are present in sample ROLE_OWNER but not in the map
    const sampleItems = userswithSOD.slice(0, 10);
    const ownersNotFound = new Set();
    
    console.log("Looking for role owners with these UUIDs in our map:", 
      Object.keys(roleOwnerIdToNameMap).slice(0, 5), 
      `(${Object.keys(roleOwnerIdToNameMap).length} total)`
    );
    
    sampleItems.forEach(item => {
      if (item.ROLE_OWNER && typeof item.ROLE_OWNER === 'string') {
        console.log(`Checking ROLE_OWNER: "${item.ROLE_OWNER}" for role "${item.ROLE_NAME}"`);
        console.log(`- Is in map: ${roleOwnerIdToNameMap[item.ROLE_OWNER] ? 'YES' : 'NO'}`);
        
        // Check if the UUID format might be different
        const normalizedUUID = item.ROLE_OWNER.toLowerCase().trim();
        const matchingKey = Object.keys(roleOwnerIdToNameMap).find(key => 
          key.toLowerCase().trim() === normalizedUUID
        );
        
        if (matchingKey) {
          console.log(`- Found match with normalized UUID: ${matchingKey}`);
          // Add this to our map with the original UUID
          roleOwnerIdToNameMap[item.ROLE_OWNER] = roleOwnerIdToNameMap[matchingKey];
        } else if (!roleOwnerIdToNameMap[item.ROLE_OWNER]) {
          ownersNotFound.add(item.ROLE_OWNER);
        }
      }
    });
    
    if (ownersNotFound.size > 0) {
      console.warn("Sample UUIDs not found in roleOwnerList:", Array.from(ownersNotFound));
    }
    
    // Helper function to get owner name from UUID or object
    const getOwnerName = (ownerIdentifier, userData) => {
      // First check if we have the new fields in the userData
      if (userData && (userData.ROLE_OWNER_FIRST_NAME || userData.ROLE_OWNER_LAST_NAME)) {
        // If we have ROLE_OWNER_EMAIL, use that instead of the user's EMAIL_ADDRESS
        return {
          name: `${userData.ROLE_OWNER_FIRST_NAME || ''} ${userData.ROLE_OWNER_LAST_NAME || ''}`.trim(),
          fullName: `${userData.ROLE_OWNER_FIRST_NAME || ''} ${userData.ROLE_OWNER_LAST_NAME || ''}`.trim(),
          email: userData.ROLE_OWNER_EMAIL || userData.EMAIL_ADDRESS || ''
        };
      }
      
      if (!ownerIdentifier) return { name: 'Owner Not Found', email: '' };
      
      // Direct match in roleOwnerList by UUID
      if (typeof ownerIdentifier === 'string' && roleOwnerIdToNameMap[ownerIdentifier]) {
        const matchingOwner = roleOwnerList.find(owner => owner.value === ownerIdentifier);
        
        // Extract just the name part from "Name (email@example.com)" format
        const fullLabel = roleOwnerIdToNameMap[ownerIdentifier];
        const nameOnly = fullLabel.includes('(') ? fullLabel.split('(')[0].trim() : fullLabel;
        
        // Try to extract email from the label if it's in the format "Name (email@example.com)"
        let email = '';
        if (fullLabel.includes('(') && fullLabel.includes(')')) {
          const emailMatch = fullLabel.match(/\(([^)]+)\)/);
          if (emailMatch && emailMatch[1] && emailMatch[1].includes('@')) {
            email = emailMatch[1];
          }
        }
        
        return { 
          name: nameOnly,
          fullName: fullLabel,
          email: matchingOwner?.email || email || ''
        };
      }
      
      if (!ownerIdentifier) return { name: 'Owner Not Found', email: '' };
      
      // Direct match in roleOwnerList by UUID
      if (typeof ownerIdentifier === 'string' && roleOwnerIdToNameMap[ownerIdentifier]) {
        const matchingOwner = roleOwnerList.find(owner => owner.value === ownerIdentifier);
        
        // Extract just the name part from "Name (email@example.com)" format
        const fullLabel = roleOwnerIdToNameMap[ownerIdentifier];
        const nameOnly = fullLabel.includes('(') ? fullLabel.split('(')[0].trim() : fullLabel;
        
        return { 
          name: nameOnly,
          fullName: fullLabel,
          email: matchingOwner?.email || ''
        };
      }
      
      // If it's an object with name fields
      if (typeof ownerIdentifier === 'object') {
        if (ownerIdentifier.FIRST_NAME || ownerIdentifier.LAST_NAME) {
          return {
            name: `${ownerIdentifier.FIRST_NAME || ''} ${ownerIdentifier.LAST_NAME || ''}`.trim() || 'Owner Not Found',
            fullName: `${ownerIdentifier.FIRST_NAME || ''} ${ownerIdentifier.LAST_NAME || ''}`.trim() || 'Owner Not Found',
            email: ownerIdentifier.EMAIL_ADDRESS || ''
          };
        }
        
        if (ownerIdentifier.label) {
          // Extract just the name part from "Name (email@example.com)" format
          const fullLabel = ownerIdentifier.label;
          const nameOnly = fullLabel.includes('(') ? fullLabel.split('(')[0].trim() : fullLabel;
          
          return {
            name: nameOnly,
            fullName: fullLabel,
            email: ownerIdentifier.email || ''
          };
        }
      }
      
      // Debug why we couldn't find this owner
      if (typeof ownerIdentifier === 'string') {
        console.warn(`Could not find owner for UUID: ${ownerIdentifier}`);
      }
      
      return { name: 'Owner Not Found', fullName: 'Owner Not Found', email: '' };
    };

    // Process all data 
    userswithSOD.forEach(item => {
      // Process roles
      const roleName = item.ROLE_NAME;
      if (roleName) {
        if (!roles[roleName]) {
          // Extract role owner information properly
          const ownerInfo = getOwnerName(item.ROLE_OWNER, item);
          
          roles[roleName] = {
            name: roleName,
            totalUsers: 0,
            reviewedUsers: 0,
            rejectedUsers: 0,  // Track rejected users separately
            status: item.STATUS || 'Not Started',
            ownerEmail: ownerInfo.email,
            ownerName: ownerInfo.name,
            ownerUUID: typeof item.ROLE_OWNER === 'string' ? item.ROLE_OWNER : 
                      (item.ROLE_OWNER && item.ROLE_OWNER.id ? item.ROLE_OWNER.id : null),
            lastReviewed: item.REVIEW_COMPLETED_ON,
            withSodViolation: item.WITH_SOD_VIOLATION ? 1 : 0,
            id: `role-${roleName.replace(/\s+/g, '-').toLowerCase()}`
          };
        }
        roles[roleName].totalUsers++;
        if (item.STATUS === 'Reviewed' || item.STATUS === 'Approved') {
          roles[roleName].reviewedUsers++;
        } else if (item.STATUS === 'Rejected') {
          roles[roleName].rejectedUsers++;  // Count rejected separately
        }
        if (item.WITH_SOD_VIOLATION) {
          roles[roleName].withSodViolation++;
        }
      }

      // Process users
      if (item.EMAIL_ADDRESS) {
        if (!users[item.EMAIL_ADDRESS]) {
          // Find user record in appRecord by email address
          const userRecord = appRecord.find(record => record.EMAIL_ADDRESS === item.EMAIL_ADDRESS);
          
          users[item.EMAIL_ADDRESS] = {
            id: `user-${item.EMAIL_ADDRESS.replace(/[@.]/g, '-')}`,
            email: item.EMAIL_ADDRESS,
            firstName: userRecord?.FIRST_NAME || item.FIRST_NAME || '',
            lastName: userRecord?.LAST_NAME || item.LAST_NAME || '',
            roles: [],
            totalRoles: 0,
            reviewedRoles: 0,
            rejectedRoles: 0,  // Track rejected roles separately
            roleOwners: new Set(), // Track unique role owners for this user
            roleOwnerUUIDs: new Set(), // Track role owner UUIDs
            ownerNamesByUUID: {} // Map UUID to name for direct lookup
          };
        }
        
        // Extract role owner information properly
        const ownerInfo = getOwnerName(item.ROLE_OWNER, item);
        const ownerUUID = typeof item.ROLE_OWNER === 'string' ? item.ROLE_OWNER : 
                         (item.ROLE_OWNER && item.ROLE_OWNER.id ? item.ROLE_OWNER.id : null);
        
        users[item.EMAIL_ADDRESS].roles.push({
          roleName: item.ROLE_NAME,
          status: item.STATUS || 'Not Started',
          reviewDate: item.REVIEW_COMPLETED_ON,
          withSodViolation: item.WITH_SOD_VIOLATION,
          ownerEmail: ownerInfo.email,
          ownerName: ownerInfo.name,
          ownerUUID: ownerUUID,
          // Add the new role owner fields if they exist
          roleOwnerFirstName: item.ROLE_OWNER_FIRST_NAME || '',
          roleOwnerLastName: item.ROLE_OWNER_LAST_NAME || ''
        });
        
        users[item.EMAIL_ADDRESS].totalRoles++;
        if (item.STATUS === 'Reviewed' || item.STATUS === 'Approved') {
          users[item.EMAIL_ADDRESS].reviewedRoles++;
        } else if (item.STATUS === 'Rejected') {
          users[item.EMAIL_ADDRESS].rejectedRoles++;  // Count rejected separately
        }
        
        // Add role owner to the set of owners for this user ONLY if not "Owner Not Found"
        if (ownerInfo.name && ownerInfo.name !== 'Owner Not Found') {
          users[item.EMAIL_ADDRESS].roleOwners.add(ownerInfo.name);
          
          // Also keep a direct map of UUID to name
          if (ownerUUID) {
            users[item.EMAIL_ADDRESS].ownerNamesByUUID[ownerUUID] = ownerInfo.name;
          }
        }
        
        // Store UUID regardless of whether we found a name (we might resolve it later)
        if (ownerUUID) {
          users[item.EMAIL_ADDRESS].roleOwnerUUIDs.add(ownerUUID);
        }
      }

      // Process role owners
      if (item.ROLE_OWNER) {
        const ownerInfo = getOwnerName(item.ROLE_OWNER, item);
        const ownerUUID = typeof item.ROLE_OWNER === 'string' ? item.ROLE_OWNER : 
                         (item.ROLE_OWNER && item.ROLE_OWNER.id ? item.ROLE_OWNER.id : null);
        
        if (ownerUUID && !roleOwners[ownerUUID]) {
          // Get email from various possible sources
          const ownerEmail = item.ROLE_OWNER_EMAIL || ownerInfo.email || '';
          
          roleOwners[ownerUUID] = {
            id: `owner-${ownerUUID}`,
            email: ownerEmail,
            name: ownerInfo.name,
            uuid: ownerUUID,
            totalRolesAssigned: 0,
            reviewedRolesCount: 0,
            assignedRoles: new Set(),
          };
        }
        
        if (ownerUUID && roleOwners[ownerUUID] && !roleOwners[ownerUUID].assignedRoles.has(item.ROLE_NAME)) {
          roleOwners[ownerUUID].assignedRoles.add(item.ROLE_NAME);
          roleOwners[ownerUUID].totalRolesAssigned++;
        }
      }
    });
    
    // Log some statistics about our results
    const ownerStats = {
      rolesWithValidOwner: Object.values(roles).filter(r => r.ownerName !== 'Owner Not Found').length,
      rolesWithoutOwner: Object.values(roles).filter(r => r.ownerName === 'Owner Not Found').length,
      totalRoles: Object.keys(roles).length,
    };
    
    console.log("Owner resolution stats:", ownerStats);

    // Calculate completion percentages
    Object.values(roles).forEach(role => {
      // Include both reviewed and rejected users in the completion calculation
      const completedUsers = role.reviewedUsers + role.rejectedUsers;
      role.completionPercent = role.totalUsers > 0 ? Math.round((completedUsers / role.totalUsers) * 100) : 0;
      // Track if any rejections exist
      role.hasRejections = role.rejectedUsers > 0;
    });

    // Get roles pending review (not fully reviewed)
    const pendingRoles = Object.values(roles)
      .filter(role => role.completionPercent < 100)
      .sort((a, b) => a.completionPercent - b.completionPercent);

    // Get users and their role status
    const usersList = Object.values(users).map(user => {
      // Calculate completion percentage including rejected roles
      const completedRoles = user.reviewedRoles + user.rejectedRoles;
      const completionPercent = user.totalRoles > 0 ? Math.round((completedRoles / user.totalRoles) * 100) : 0;
      // Track if any roles are rejected
      const hasRejectedRoles = user.rejectedRoles > 0;
      
      // Try multiple methods to find primary role owner
      const roleOwnersArray = Array.from(user.roleOwners);
      let primaryOwner = 'Owner Not Found';
      let allOwnerNames = [];
      
      // Console log for debugging
      console.log(`Processing user ${user.email}:`);
      console.log(`- Role owners in set:`, roleOwnersArray);
      console.log(`- Role owner UUIDs:`, Array.from(user.roleOwnerUUIDs));
      
      // Method 1: Use any existing name in the roleOwners set
      if (roleOwnersArray.length > 0) {
        primaryOwner = roleOwnersArray[0];
        allOwnerNames = roleOwnersArray;
        console.log(`- Using owner from set: ${primaryOwner}`);
      } 
      // Method 2: Look up names from UUIDs using roleOwnerList
      else if (user.roleOwnerUUIDs.size > 0) {
        const uuids = Array.from(user.roleOwnerUUIDs);
        console.log(`- Looking up owners for UUIDs:`, uuids);
        
        // Try to find names for each UUID
        const ownersFound = uuids.map(uuid => {
          // First check our mapping from when we processed roles
          if (user.ownerNamesByUUID && user.ownerNamesByUUID[uuid]) {
            return user.ownerNamesByUUID[uuid];
          }
          
          // Then look in the roleOwnerList
          if (roleOwnerList && roleOwnerList.length) {
            const matchingOwner = roleOwnerList.find(owner => owner.value === uuid);
            if (matchingOwner) {
              return matchingOwner.label;
            }
          }
          
          // Finally check global map
          if (roleOwnerIdToNameMap && roleOwnerIdToNameMap[uuid]) {
            return roleOwnerIdToNameMap[uuid];
          }
          
          return null;
        }).filter(Boolean); // Remove null values
        
        if (ownersFound.length > 0) {
          primaryOwner = ownersFound[0];
          allOwnerNames = ownersFound;
          console.log(`- Found owners from UUID lookup:`, ownersFound);
        }
      }
      
      // Method 3: Look through roles for any owner
      if (primaryOwner === 'Owner Not Found' && user.roles && user.roles.length > 0) {
        // Find roles with valid owners
        const rolesWithOwners = user.roles.filter(role => 
          role.ownerName && role.ownerName !== 'Owner Not Found'
        );
        
        if (rolesWithOwners.length > 0) {
          const ownerNames = [...new Set(rolesWithOwners.map(role => role.ownerName))];
          primaryOwner = ownerNames[0];
          allOwnerNames = ownerNames;
          console.log(`- Found owners from roles:`, ownerNames);
        }
      }
      
      console.log(`- Final primary owner for ${user.email}:`, primaryOwner);
      
      // Special check for our known problem user
      if (user.email === 'ethan.perez@example.com') {
        console.log('SPECIAL DEBUG FOR ETHAN:', {
          roleOwnersSet: user.roleOwners,
          roleOwnersArray,
          primaryOwner,
          allOwnerNames
        });
      }
      
      const userData = {
        ...user,
        completionPercent: completionPercent,
        hasRejectedRoles: hasRejectedRoles,  // Add flag to identify if any roles are rejected
        name: `${user.firstName} ${user.lastName}`.trim() || user.email,
        rolesList: user.roles.map(r => r.roleName).join(', '),
        roleOwners: allOwnerNames,
        roleOwnerUUIDs: Array.from(user.roleOwnerUUIDs),
        primaryRoleOwner: primaryOwner
      };
      
      // Special case check for debugging
      if (userData.email === 'ethan.perez@example.com') {
        console.log('FINAL USER DATA FOR ETHAN:', {
          primaryRoleOwner: userData.primaryRoleOwner,
          roleOwners: userData.roleOwners,
          completionPercent: userData.completionPercent,
          hasRejectedRoles: userData.hasRejectedRoles
        });
      }
      
      return userData;
    });

    // Get role owners who haven't completed reviews
    Object.values(roleOwners).forEach(owner => {
      owner.assignedRoles.forEach(roleName => {
        const role = roles[roleName];
        if (role && role.completionPercent === 100) {
          owner.reviewedRolesCount++;
        }
      });
      owner.completionPercent = owner.totalRolesAssigned > 0 ? Math.round((owner.reviewedRolesCount / owner.totalRolesAssigned) * 100) : 0;
      owner.rolesArray = Array.from(owner.assignedRoles);
    });

    const pendingRoleOwners = Object.values(roleOwners)
      .filter(owner => owner.completionPercent < 100)
      .sort((a, b) => a.completionPercent - b.completionPercent);

    // Summary statistics
    const stats = {
      totalRoles: Object.keys(roles).length,
      pendingRoles: Object.values(roles).filter(role => role.completionPercent < 100).length,
      approvedRoles: Object.values(roles).filter(role => role.reviewedUsers > 0 && role.rejectedUsers === 0).length,
      rejectedRoles: Object.values(roles).filter(role => role.rejectedUsers > 0).length,
      completedRoles: Object.values(roles).filter(role => role.completionPercent === 100).length,
      totalUsers: Object.keys(users).length,
      totalRoleOwners: Object.keys(roleOwners).length,
      pendingRoleOwners: pendingRoleOwners.length,
      rolesWithSodViolation: Object.values(roles).filter(role => role.withSodViolation > 0).length,
    };

    // Chart data - update to properly separate Approved, Rejected, and Pending
    const roleStatusData = [
      { id: 0, value: stats.approvedRoles, label: 'Approved', color: '#4caf50' },
      { id: 1, value: stats.rejectedRoles, label: 'Rejected', color: '#f44336' },
      { id: 2, value: stats.pendingRoles, label: 'Pending', color: '#ff9800' },
    ];

    // Add rejected users monitoring data
    const rejectedUsersMonitoring = [];
    Object.values(users).forEach(user => {
      // Only track users with rejected roles
      if (user.rejectedRoles > 0) {
        // Look up in APP_RECORD to see if they're still active
        const appRecordUser = appRecord.find(record => record.EMAIL_ADDRESS === user.email);
        
        if (appRecordUser && appRecordUser.IS_ACTIVE !== 'N') {
          rejectedUsersMonitoring.push({
            ...user,
            appRecordStatus: appRecordUser.IS_ACTIVE || 'Y',
            appRecordRole: appRecordUser.ROLE_NAME || 'Unknown',
            lastUpdated: appRecordUser.UPDATED_ON || 'Unknown',
            department: appRecordUser.DEPARTMENT || 'Unknown',
            rejectedRoleDetails: user.roles.filter(role => 
              role.status === 'Rejected'
            ).map(role => ({
              roleName: role.roleName,
              reviewDate: role.reviewDate,
              ownerName: role.ownerName
            }))
          });
        }
      }
    });

    return {
      roles: Object.values(roles),
      pendingRoles,
      allUsers: usersList,
      pendingRoleOwners,
      stats,
      roleStatusData,
      rejectedUsersMonitoring,
      appName: appRecord && appRecord.length > 0 ? (appRecord[0].APP_NAME || 'Application') : 'Application'
    };
  }, [userswithSOD, appRecord, roleOwnerList]);

  // Filter users based on selectedRoleFilter, search query, and status filters
  const filteredUsers = useMemo(() => {
    // First apply role filter - now checks if user has ANY of the selected roles
    let users = selectedRoleFilter.length > 0
      ? dashboardData.allUsers.filter(user => 
          user.roles.some(role => selectedRoleFilter.includes(role.roleName))
        )
      : dashboardData.allUsers;
    
    // Then apply status filters
    users = users.filter(user => {
      const isCompleted = user.completionPercent === 100;
      const hasRejections = user.hasRejectedRoles;
      
      if (!statusFilters.approved && isCompleted && !hasRejections) return false;
      if (!statusFilters.rejected && isCompleted && hasRejections) return false;
      if (!statusFilters.pending && !isCompleted) return false;
      
      return true;
    });
    
    // Then apply search filter if there's a search query
    if (searchQuery.trim()) {
      const lowercaseQuery = searchQuery.toLowerCase().trim();
      users = users.filter(user => {
        // Search in name and email
        if (user.name?.toLowerCase().includes(lowercaseQuery) || 
            user.email?.toLowerCase().includes(lowercaseQuery)) {
          return true;
        }
        // Search in roles 
        if (user.rolesList?.toLowerCase().includes(lowercaseQuery)) {
          return true;
        }
        // Search in role owner
        if (user.primaryRoleOwner?.toLowerCase().includes(lowercaseQuery)) {
          return true;
        }
        return false;
      });
    }
    
    return users;
  }, [selectedRoleFilter, dashboardData.allUsers, searchQuery, statusFilters]);

  // Special check just before rendering to make sure all user data has the correct role owners
  const finalFilteredUsers = useMemo(() => {
    return filteredUsers.map(user => {
      // If the user has valid roleOwners but primaryRoleOwner is "Owner Not Found"
      if (user.roleOwners && user.roleOwners.length > 0 && 
          (!user.primaryRoleOwner || user.primaryRoleOwner === 'Owner Not Found')) {
        // Make a fixed copy with the correct primaryRoleOwner
        return {
          ...user,
          primaryRoleOwner: user.roleOwners[0]
        };
      }
      return user;
    });
  }, [filteredUsers]);

  // Handle role selection for filtering - update to add/remove from array
  const handleRoleClick = (roleName) => {
    setSelectedRoleFilter(prev => {
      // If role is already selected, remove it
      if (prev.includes(roleName)) {
        return prev.filter(role => role !== roleName);
      }
      // Otherwise add it to the selected roles
      return [...prev, roleName];
    });
    // Don't reset search when changing role filter
  };

  // Clear role filter - now clears the array
  const clearRoleFilter = () => {
    setSelectedRoleFilter([]);
    // Don't reset search when clearing role filter
  };

  // Handle clear single role from filter
  const handleRemoveRoleFilter = (roleToRemove) => {
    setSelectedRoleFilter(prev => prev.filter(role => role !== roleToRemove));
  };

  // Handle clear search query
  const handleClearSearch = () => {
    setSearchQuery('');
  };

  // Handle search query change
  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  // Add role search functionality
  const [roleSearchQuery, setRoleSearchQuery] = useState('');
  
  // Handle role search query change
  const handleRoleSearchChange = (event) => {
    setRoleSearchQuery(event.target.value);
  };
  
  // Handle clear role search query
  const handleClearRoleSearch = () => {
    setRoleSearchQuery('');
  };
  
  // Filter roles based on search query
  const filteredPendingRoles = useMemo(() => {
    if (!roleSearchQuery.trim()) {
      return dashboardData.pendingRoles;
    }
    
    const lowercaseQuery = roleSearchQuery.toLowerCase().trim();
    return dashboardData.pendingRoles.filter(role => 
      role.name.toLowerCase().includes(lowercaseQuery) || 
      (role.ownerName && role.ownerName.toLowerCase().includes(lowercaseQuery))
    );
  }, [dashboardData.pendingRoles, roleSearchQuery]);

  // Define searchable columns for the quick filter
  const getStatusChipProps = (status) => {
    switch (status) {
      case 'Approved':
      case 'Reviewed':
        return { label: 'Reviewed', color: 'success', icon: <CheckCircle /> };
      case 'Rejected':
      case 'Action Needed':
        return { label: 'Action Needed', color: 'error', icon: <Error /> };
      case 'Pending Review':
        return { label: 'Pending', color: 'warning', icon: <Pending /> };
      default:
        return { label: 'Not Started', color: 'default', icon: <AccessTime /> };
    }
  };

  // User table columns
  const userColumns = [
    { 
      field: 'name', 
      headerName: 'User', 
      flex: 1,
      minWidth: 180,
      sortable: true,
      renderCell: (params) => (
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          width: '100%', 
          height: '100%',
          pl: 1 
        }}>
          <Avatar 
            sx={{ 
              width: 28, 
              height: 28, 
              bgcolor: alpha(params.row.completionPercent === 100 ? '#4caf50' : '#1976d2', 0.1),
              color: params.row.completionPercent === 100 ? '#4caf50' : '#1976d2',
              mr: 1.5,
              fontSize: '0.775rem'
            }}
          >
            {params.row.firstName?.[0] || params.row.email?.[0]?.toUpperCase() || 'U'}
          </Avatar>
          <Box sx={{ lineHeight: 1.2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', display: 'block' }}>
              {params.value}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
              {params.row.email}
            </Typography>
          </Box>
        </Box>
      )
    },
    { 
      field: 'primaryRoleOwner', 
      headerName: 'Role Owner', 
      width: 180,
      sortable: true,
      valueGetter: (params) => {
        try {
          // Absolute safeguard - if anything goes wrong, return "Owner Not Found"
          if (!params) return 'Owner Not Found';
          
          // Verify row exists
          const row = params.row;
          if (!row) return 'Owner Not Found';
          
          // Check if user has any roles with the new fields
          if (row.roles && Array.isArray(row.roles) && row.roles.length > 0) {
            const rolesWithOwnerNames = row.roles.filter(role => 
              role.roleOwnerFirstName || role.roleOwnerLastName
            );
            
            if (rolesWithOwnerNames.length > 0) {
              const firstRole = rolesWithOwnerNames[0];
              const ownerName = `${firstRole.roleOwnerFirstName || ''} ${firstRole.roleOwnerLastName || ''}`.trim();
              if (ownerName) return ownerName;
            }
          }
          
          // Safely handle roleOwners array
          if (row.roleOwners && Array.isArray(row.roleOwners) && row.roleOwners.length > 0) {
            // Extract just the name if in "Name (email)" format
            const ownerName = row.roleOwners[0];
            return ownerName.includes('(') ? ownerName.split('(')[0].trim() : ownerName;
          }
          
          // Fallback to primaryRoleOwner if it exists, also extracting just the name
          const primaryOwner = row.primaryRoleOwner || 'Owner Not Found';
          return primaryOwner.includes('(') ? primaryOwner.split('(')[0].trim() : primaryOwner;
        } catch (error) {
          // Any unexpected error, return "Owner Not Found"
          console.error("Error in primaryRoleOwner valueGetter:", error);
          return 'Owner Not Found';
        }
      },
      renderCell: (params) => {
        try {
          // Another failsafe check
          if (!params || !params.row) {
            return <Typography variant="body2">Owner Not Found</Typography>;
          }
          
          // Safely get row data
          const row = params.row;
          
          // Helper to extract just the name part from "Name (email)" format
          const extractName = (fullName) => {
            if (!fullName) return 'Owner Not Found';
            return fullName.includes('(') ? fullName.split('(')[0].trim() : fullName;
          };
          
          // First check for the new fields in the roles
          if (row.roles && Array.isArray(row.roles) && row.roles.length > 0) {
            const rolesWithOwnerNames = row.roles.filter(role => 
              role.roleOwnerFirstName || role.roleOwnerLastName
            );
            
            if (rolesWithOwnerNames.length > 0) {
              const roleOwnerNames = rolesWithOwnerNames.map(role => 
                `${role.roleOwnerFirstName || ''} ${role.roleOwnerLastName || ''}`.trim()
              ).filter(Boolean);
              
              if (roleOwnerNames.length > 0) {
                const uniqueOwnerNames = [...new Set(roleOwnerNames)];
                
                // Render with multiple owners or single owner
                return (
                  <Box sx={{ px: 1, py: 0.5, width: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {uniqueOwnerNames.length > 1 ? (
                      <Tooltip 
                        title={
                          <Box>
                            <Typography variant="caption" sx={{ fontWeight: 600 }}>Role Owners:</Typography>
                            {uniqueOwnerNames.map((owner, idx) => (
                              <Typography key={idx} variant="caption" component="div">
                                {owner}
                              </Typography>
                            ))}
                          </Box>
                        }
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              mr: 0.5, 
                              color: 'text.primary', 
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              maxWidth: '140px'
                            }}
                          >
                            {uniqueOwnerNames[0]}
                          </Typography>
                          <Chip 
                            label={`+${uniqueOwnerNames.length - 1}`} 
                            size="small" 
                            sx={{ 
                              height: 16, 
                              fontSize: '0.625rem', 
                              bgcolor: alpha('#3b82f6', 0.1),
                              color: '#3b82f6'
                            }} 
                          />
                        </Box>
                      </Tooltip>
                    ) : (
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: 'text.primary',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}
                      >
                        {uniqueOwnerNames[0]}
                      </Typography>
                    )}
                  </Box>
                );
              }
            }
          }
          
          // Determine the owner name to display with safe access - fallback to old method
          let ownerName;
          let fullOwnerName; // For tooltip
          
          if (row.roleOwners && Array.isArray(row.roleOwners) && row.roleOwners.length > 0) {
            fullOwnerName = row.roleOwners[0];
            ownerName = extractName(fullOwnerName);
          } else {
            fullOwnerName = params.value || row.primaryRoleOwner || 'Owner Not Found';
            ownerName = extractName(fullOwnerName);
          }
          
          const isOwnerNotFound = ownerName === 'Owner Not Found';
          
          // Render with multiple owners or single owner
          return (
            <Box sx={{ px: 1, py: 0.5, width: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {row.roleOwners && Array.isArray(row.roleOwners) && row.roleOwners.length > 1 ? (
                <Tooltip 
                  title={
                    <Box>
                      <Typography variant="caption" sx={{ fontWeight: 600 }}>Role Owners:</Typography>
                      {row.roleOwners.map((owner, idx) => (
                        <Typography key={idx} variant="caption" component="div">
                          {extractName(owner)}
                        </Typography>
                      ))}
                    </Box>
                  }
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        mr: 0.5, 
                        color: isOwnerNotFound ? 'error.main' : 'text.primary', 
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: '140px'
                      }}
                    >
                      {ownerName}
                    </Typography>
                    <Chip 
                      label={`+${row.roleOwners.length - 1}`} 
                      size="small" 
                      sx={{ 
                        height: 16, 
                        fontSize: '0.625rem', 
                        bgcolor: alpha('#3b82f6', 0.1),
                        color: '#3b82f6'
                      }} 
                    />
                  </Box>
                </Tooltip>
              ) : (
                <Tooltip title={fullOwnerName !== ownerName ? fullOwnerName : ""}>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: isOwnerNotFound ? 'error.main' : 'text.primary',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}
                  >
                    {ownerName}
                  </Typography>
                </Tooltip>
              )}
            </Box>
          );
        } catch (error) {
          // Catch any rendering errors
          console.error("Error in primaryRoleOwner renderCell:", error);
          return <Typography variant="body2">Owner Not Found</Typography>;
        }
      }
    },
    { 
      field: 'totalRoles', 
      headerName: 'Roles', 
      width: 60,
      align: 'center',
      headerAlign: 'center',
      sortable: true,
      type: 'number',
      renderCell: (params) => (
        <Chip 
          label={params.value} 
          size="small" 
          sx={{ 
            bgcolor: alpha("#64748b", 0.08), 
            color: '#64748b', 
            fontWeight: 500,
            height: 22,
            fontSize: '0.7rem'
          }} 
        />
      )
    },
    { 
      field: 'completionPercent', 
      headerName: 'Review Progress', 
      flex: 1,
      minWidth: 150,
      sortable: true,
      type: 'number',
      renderCell: (params) => {
        // Determine color based on completion percentage and rejection status
        const isComplete = params.value === 100;
        const hasRejections = params.row.hasRejectedRoles;
        
        // Choose colors based on status:
        // - Green for 100% approved
        // - Red for 100% but has rejections
        // - Orange for in progress
        const progressColor = isComplete 
          ? (hasRejections ? '#f44336' : '#4caf50') 
          : '#ff9800';
        
        const progressBgColor = isComplete 
          ? (hasRejections ? alpha('#f44336', 0.2) : alpha('#4caf50', 0.2)) 
          : alpha('#ff9800', 0.2);
        
        return (
          <Box sx={{ width: '100%', display: 'flex', alignItems: 'center' }}>
            <LinearProgress 
              variant="determinate" 
              value={params.value} 
              sx={{ 
                height: 5, 
                borderRadius: 3, 
                flexGrow: 1,
                mr: 1.5,
                bgcolor: progressBgColor,
                '& .MuiLinearProgress-bar': {
                  bgcolor: progressColor
                }
              }} 
            />
            <Typography variant="body2" sx={{ 
              fontWeight: 600, 
              color: progressColor, 
              minWidth: 32, 
              textAlign: 'right', 
              fontSize: '0.75rem' 
            }}>
              {`${params.value}%`}
            </Typography>
          </Box>
        );
      }
    },
    {
      field: 'rolesList',
      headerName: 'Assigned Roles',
      flex: 1,
      minWidth: 180,
      sortable: true,
      renderCell: (params) => {
        const roles = params.value.split(', ');
        return (
          <Box sx={{ px: 1, py: 0.5 }}>
            {roles.length > 0 ? (
              <Tooltip 
                title={
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>Assigned Roles:</Typography>
                    {roles.map((role, idx) => (
                      <Typography key={idx} variant="caption" component="div">
                        • {role}
                      </Typography>
                    ))}
                  </Box>
                }
              >
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {roles.length <= 2 ? (
                    roles.map((role, idx) => (
                      <Chip 
                        key={idx}
                        label={role} 
                        size="small" 
                        sx={{ 
                          fontSize: '0.675rem',
                          height: 20,
                          bgcolor: alpha('#3b82f6', 0.08),
                          color: '#3b82f6'
                        }} 
                      />
                    ))
                  ) : (
                    <>
                      <Chip 
                        label={roles[0]} 
                        size="small" 
                        sx={{ 
                          fontSize: '0.675rem',
                          height: 20,
                          bgcolor: alpha('#3b82f6', 0.08),
                          color: '#3b82f6'
                        }} 
                      />
                      <Chip 
                        label={`+${roles.length - 1} more`} 
                        size="small" 
                        sx={{ 
                          fontSize: '0.675rem',
                          height: 20,
                          bgcolor: alpha('#64748b', 0.1),
                          color: '#64748b'
                        }} 
                      />
                    </>
                  )}
                </Box>
              </Tooltip>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No roles assigned
              </Typography>
            )}
          </Box>
        );
      }
    },
    {
      field: 'actions',
      headerName: 'Details',
      width: 110,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      renderCell: (params) => (
        <Button
          variant="text"
          size="small"
          color="primary"
          startIcon={<Visibility fontSize="small" />}
          sx={{ 
            textTransform: 'none', 
            fontSize: '0.75rem', 
            py: 0.5, 
            px: 1,
            minWidth: 72,
            '&:hover': {
              bgcolor: alpha('#1976d2', 0.04)
            }
          }}
          onClick={() => handleViewUser(params.row)}
        >
          View
        </Button>
      )
    }
  ];

  // Handle view user details
  const handleViewUser = (user) => {
    setSelectedUser(user);
    setOpenUserDialog(true);
  };

  // Handle close user dialog
  const handleCloseUserDialog = () => {
    setOpenUserDialog(false);
  };

  // Handle sending a reminder for a specific role
  const handleSendRoleReminder = async (role) => {
    if (!role.ownerUUID || !role.ownerName) {
      setSnackbar({
        open: true,
        message: `Cannot send reminder: No role owner assigned to "${role.roleName}"`,
        severity: 'error'
      });
      return;
    }
    
    setIsLoading(true);
    try {
      // Extract first and last name from owner name
      let firstName = '';
      let lastName = '';
      
      if (role.ownerName) {
        const nameParts = role.ownerName.split(' ');
        if (nameParts.length >= 2) {
          firstName = nameParts[0];
          lastName = nameParts.slice(1).join(' ');
        } else {
          firstName = role.ownerName;
        }
      }
      
      // Format the data for sending to the API
      const reminderData = {
        ROLE_OWNER: role.ownerUUID,
        ROLE_OWNER_FIRST_NAME: firstName,
        ROLE_OWNER_LAST_NAME: lastName,
        ROLE_OWNER_EMAIL: role.ownerEmail,
        EMAIL: role.ownerEmail,
        ROLES: [role.roleName],
        APP_NAME: dashboardData.appName
      };
      
      await uarService.sendUARFollowUp(reminderData);
      
      setSnackbar({
        open: true,
        message: `Reminder sent successfully to ${role.ownerName} for role "${role.roleName}"`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Error sending role reminder:', error);
      setSnackbar({
        open: true,
        message: `Failed to send reminder: ${error.message || 'Unknown error'}`,
        severity: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle sending reminders for all pending roles of a user
  const handleSendUserReminders = async () => {
    if (!selectedUser || !selectedUser.roles || selectedUser.roles.length === 0) {
      return;
    }
    
    // Find roles that need review (not already reviewed/approved)
    const pendingRoles = selectedUser.roles.filter(
      role => role.status !== 'Reviewed' && role.status !== 'Approved'
    );
    
    if (pendingRoles.length === 0) {
      setSnackbar({
        open: true,
        message: 'All roles for this user have already been reviewed.',
        severity: 'info'
      });
      return;
    }

    // Show confirmation dialog
    setConfirmDialog({
      open: true,
      title: 'Send Reminders for User',
      message: `Are you sure you want to send reminders for ${pendingRoles.length} pending roles assigned to ${selectedUser.name}?`,
      onConfirm: async () => {
        await sendUserReminders(pendingRoles);
      }
    });
  };

  // Extracted function to send the actual reminders
  const sendUserReminders = async (pendingRoles) => {
    // Close the confirmation dialog
    setConfirmDialog({
      open: false,
      title: '',
      message: '',
      onConfirm: null
    });
    
    setIsLoading(true);
    
    try {
      // Group roles by role owner to send fewer API calls
      const rolesByOwner = {};
      
      pendingRoles.forEach(role => {
        // Skip roles without owners
        if (!role.ownerUUID || !role.ownerName || role.ownerName === 'Owner Not Found') {
          return;
        }
        
        if (!rolesByOwner[role.ownerUUID]) {
          // Extract first and last name from owner name
          let firstName = '';
          let lastName = '';
          
          if (role.ownerName) {
            const nameParts = role.ownerName.split(' ');
            if (nameParts.length >= 2) {
              firstName = nameParts[0];
              lastName = nameParts.slice(1).join(' ');
            } else {
              firstName = role.ownerName;
            }
          }
          
          rolesByOwner[role.ownerUUID] = {
            uuid: role.ownerUUID,
            name: role.ownerName,
            email: role.ownerEmail,
            firstName: firstName,
            lastName: lastName,
            roles: []
          };
        }
        
        rolesByOwner[role.ownerUUID].roles.push(role.roleName);
      });
      
      // Log the details to console for debugging
      console.log('Sending reminders for these role owners and roles:', rolesByOwner);
      
      // Send reminders to each owner
      let successCount = 0;
      let failureCount = 0;
      let remindersDetailsList = [];
      
      for (const ownerUUID in rolesByOwner) {
        const owner = rolesByOwner[ownerUUID];
        
        try {
          const reminderData = {
            ROLE_OWNER: owner.uuid,
            ROLE_OWNER_FIRST_NAME: owner.firstName,
            ROLE_OWNER_LAST_NAME: owner.lastName,
            ROLE_OWNER_EMAIL: owner.email,
            EMAIL: owner.email,
            ROLES: owner.roles,
            APP_NAME: dashboardData.appName
          };
          
          await uarService.sendUARFollowUp(reminderData);
          successCount++;
          remindersDetailsList.push({
            ownerName: owner.name,
            email: owner.email,
            roles: owner.roles,
            success: true
          });
        } catch (error) {
          console.error(`Error sending reminder to ${owner.name}:`, error);
          failureCount++;
          remindersDetailsList.push({
            ownerName: owner.name,
            email: owner.email,
            roles: owner.roles,
            success: false,
            error: error.message || 'Unknown error'
          });
        }
      }
      
      // Generate detailed message for the snackbar and dialog
      let detailedMessage = '';
      if (remindersDetailsList.length > 0) {
        if (remindersDetailsList.length === 1) {
          const reminder = remindersDetailsList[0];
          detailedMessage = `${reminder.success ? 'Successfully sent' : 'Failed to send'} reminder to ${reminder.ownerName} for ${reminder.roles.length} role${reminder.roles.length !== 1 ? 's' : ''}: ${reminder.roles.join(', ')}.`;
        } else {
          detailedMessage = 'Reminders sent to the following role owners:\n\n';
          remindersDetailsList.forEach(reminder => {
            detailedMessage += `• ${reminder.ownerName}: ${reminder.success ? '✓ Success' : '✗ Failed'} - ${reminder.roles.length} role${reminder.roles.length !== 1 ? 's' : ''}\n`;
          });
        }
      }
      
      // Show appropriate message based on results
      if (successCount > 0 && failureCount === 0) {
        setSnackbar({
          open: true,
          message: `Reminders sent successfully to ${successCount} role owner${successCount !== 1 ? 's' : ''} for ${selectedUser.name}.`,
          severity: 'success'
        });
        
        // Show dialog with details if multiple owners were notified
        if (successCount > 1) {
          setConfirmDialog({
            open: true,
            title: 'Reminders Sent Successfully',
            message: detailedMessage,
            onConfirm: () => {
              setConfirmDialog({
                open: false,
                title: '',
                message: '',
                onConfirm: null
              });
            }
          });
        }
      } else if (successCount > 0 && failureCount > 0) {
        setSnackbar({
          open: true,
          message: `Reminders sent to ${successCount} role owner${successCount !== 1 ? 's' : ''}, but failed for ${failureCount}.`,
          severity: 'warning'
        });
        
        // Show dialog with details of which ones succeeded and failed
        setConfirmDialog({
          open: true,
          title: 'Partial Success Sending Reminders',
          message: detailedMessage,
          onConfirm: () => {
            setConfirmDialog({
              open: false,
              title: '',
              message: '',
              onConfirm: null
            });
          }
        });
      } else if (successCount === 0 && Object.keys(rolesByOwner).length > 0) {
        setSnackbar({
          open: true,
          message: 'Failed to send reminders to any role owners.',
          severity: 'error'
        });
        
        // Show error details
        setConfirmDialog({
          open: true,
          title: 'Failed to Send Reminders',
          message: detailedMessage,
          onConfirm: () => {
            setConfirmDialog({
              open: false,
              title: '',
              message: '',
              onConfirm: null
            });
          }
        });
      } else {
        setSnackbar({
          open: true,
          message: 'No valid role owners found to send reminders to.',
          severity: 'warning'
        });
      }
    } catch (error) {
      console.error('Error in user reminders process:', error);
      setSnackbar({
        open: true,
        message: `Failed to send reminders: ${error.message || 'Unknown error'}`,
        severity: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle sending a reminder to a specific role owner
  const handleSendReminder = async (owner) => {
    // Show confirmation dialog first
    setConfirmDialog({
      open: true,
      title: 'Send Reminder',
      message: `Are you sure you want to send a reminder to ${owner.name} about ${owner.assignedRoles.size} pending role${owner.assignedRoles.size !== 1 ? 's' : ''}?`,
      onConfirm: async () => {
        await sendReminderToOwner(owner);
      }
    });
  };

  const sendReminderToOwner = async (owner) => {
    // Close the confirmation dialog
    setConfirmDialog({
      open: false,
      title: '',
      message: '',
      onConfirm: null
    });
    
    setIsLoading(true);
    try {
      // Extract first and last name from owner name
      let firstName = '';
      let lastName = '';
      
      if (owner.name) {
        const nameParts = owner.name.split(' ');
        if (nameParts.length >= 2) {
          firstName = nameParts[0];
          lastName = nameParts.slice(1).join(' ');
        } else {
          firstName = owner.name;
        }
      }
      
      const rolesArray = Array.from(owner.assignedRoles);
      
      // Format the data for sending to the API
      const reminderData = {
        ROLE_OWNER: owner.uuid,
        ROLE_OWNER_FIRST_NAME: firstName,
        ROLE_OWNER_LAST_NAME: lastName,
        ROLE_OWNER_EMAIL: owner.email,
        EMAIL: owner.email,
        ROLES: rolesArray,
        APP_NAME: dashboardData.appName
      };
      
      console.log(`Sending reminder to ${owner.name} for roles:`, rolesArray);
      const response = await uarService.sendUARFollowUp(reminderData);
      
      // Show more detailed success message
      const roleList = rolesArray.length <= 3 
        ? rolesArray.join(', ') 
        : `${rolesArray.slice(0, 3).join(', ')} and ${rolesArray.length - 3} more`;
        
      setSnackbar({
        open: true,
        message: `Reminder sent successfully to ${owner.name} for ${rolesArray.length} role${rolesArray.length !== 1 ? 's' : ''}: ${roleList}`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Error sending reminder:', error);
      setSnackbar({
        open: true,
        message: `Failed to send reminder: ${error.message || 'Unknown error'}`,
        severity: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle sending reminders to all pending role owners
  const handleSendBulkReminders = async () => {
    // Close the confirmation dialog
    setConfirmDialog({
      open: false,
      title: '',
      message: '',
      onConfirm: null
    });
    
    setIsLoading(true);
    
    try {
      // Track success and failure counts
      let successCount = 0;
      let failureCount = 0;
      let remindersDetailsList = [];
      
      // Process each pending role owner
      for (const owner of dashboardData.pendingRoleOwners) {
        try {
          // Extract first and last name from owner name
          let firstName = '';
          let lastName = '';
          
          if (owner.name) {
            const nameParts = owner.name.split(' ');
            if (nameParts.length >= 2) {
              firstName = nameParts[0];
              lastName = nameParts.slice(1).join(' ');
            } else {
              firstName = owner.name;
            }
          }
          
          const rolesArray = Array.from(owner.assignedRoles);
          
          // Format the data for sending to the API
          const reminderData = {
            ROLE_OWNER: owner.uuid,
            ROLE_OWNER_FIRST_NAME: firstName,
            ROLE_OWNER_LAST_NAME: lastName,
            ROLE_OWNER_EMAIL: owner.email,
            EMAIL: owner.email,
            ROLES: rolesArray,
            APP_NAME: dashboardData.appName
          };
          
          console.log(`Sending reminder to ${owner.name} for roles:`, rolesArray);
          await uarService.sendUARFollowUp(reminderData);
          successCount++;
          remindersDetailsList.push({
            ownerName: owner.name,
            email: owner.email,
            rolesCount: rolesArray.length,
            success: true
          });
        } catch (error) {
          console.error(`Error sending reminder to ${owner.name}:`, error);
          failureCount++;
          remindersDetailsList.push({
            ownerName: owner.name,
            email: owner.email,
            rolesCount: Array.from(owner.assignedRoles).length,
            success: false,
            error: error.message || 'Unknown error'
          });
        }
      }
      
      // Generate detailed message for the dialog
      let detailedMessage = '';
      if (remindersDetailsList.length > 0) {
        detailedMessage = 'Results of reminder operation:\n\n';
        remindersDetailsList.forEach(reminder => {
          detailedMessage += `• ${reminder.ownerName}: ${reminder.success ? '✓ Success' : '✗ Failed'} - ${reminder.rolesCount} role${reminder.rolesCount !== 1 ? 's' : ''}\n`;
        });
      }
      
      // Show appropriate message based on results
      if (successCount > 0 && failureCount === 0) {
        setSnackbar({
          open: true,
          message: `Reminders sent successfully to all ${successCount} role owners.`,
          severity: 'success'
        });
        
        // If we sent to many owners, show detailed results
        if (successCount > 5) {
          setConfirmDialog({
            open: true,
            title: 'Reminders Sent Successfully',
            message: detailedMessage,
            onConfirm: () => {
              setConfirmDialog({
                open: false,
                title: '',
                message: '',
                onConfirm: null
              });
            }
          });
        }
      } else if (successCount > 0 && failureCount > 0) {
        setSnackbar({
          open: true,
          message: `Reminders sent to ${successCount} role owners, but failed for ${failureCount} role owners.`,
          severity: 'warning'
        });
        
        // Show detailed results for partial success
        setConfirmDialog({
          open: true,
          title: 'Partial Success Sending Reminders',
          message: detailedMessage,
          onConfirm: () => {
            setConfirmDialog({
              open: false,
              title: '',
              message: '',
              onConfirm: null
            });
          }
        });
      } else {
        setSnackbar({
          open: true,
          message: 'Failed to send any reminders.',
          severity: 'error'
        });
        
        // Show error details
        setConfirmDialog({
          open: true,
          title: 'Failed to Send Reminders',
          message: detailedMessage,
          onConfirm: () => {
            setConfirmDialog({
              open: false,
              title: '',
              message: '',
              onConfirm: null
            });
          }
        });
      }
    } catch (error) {
      console.error('Error in bulk reminder process:', error);
      setSnackbar({
        open: true,
        message: `Failed to send reminders: ${error.message || 'Unknown error'}`,
        severity: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  // Show confirmation dialog before sending bulk reminders
  const confirmBulkReminders = () => {
    setConfirmDialog({
      open: true,
      title: 'Send Bulk Reminders',
      message: `Are you sure you want to send reminder emails to all ${dashboardData.pendingRoleOwners.length} role owners with pending reviews?`,
      onConfirm: handleSendBulkReminders
    });
  };

  // Handle close confirmation dialog
  const handleCloseConfirmDialog = () => {
    setConfirmDialog({
      open: false,
      title: '',
      message: '',
      onConfirm: null
    });
  };
  
  // Handle snackbar close
  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar({
      open: false,
      message: '',
      severity: 'success'
    });
  };

  // Role status indicator
  const getRoleStatusProps = (status) => {
    switch (status) {
      case 'Approved':
      case 'Reviewed':
        return { 
          icon: <CheckCircleOutline fontSize="small" sx={{ color: '#4caf50' }} />,
          label: 'Reviewed',
          color: '#4caf50',
          bg: alpha('#4caf50', 0.1)
        };
      case 'Rejected':
      case 'Action Needed':
        return { 
          icon: <ErrorOutline fontSize="small" sx={{ color: '#f44336' }} />,
          label: 'Action Needed',
          color: '#f44336',
          bg: alpha('#f44336', 0.1)
        };
      case 'Pending Review':
        return { 
          icon: <Pending fontSize="small" sx={{ color: '#ff9800' }} />,
          label: 'Pending',
          color: '#ff9800',
          bg: alpha('#ff9800', 0.1)
        };
      default:
        return { 
          icon: <HourglassEmpty fontSize="small" sx={{ color: '#9e9e9e' }} />,
          label: 'Not Started',
          color: '#9e9e9e',
          bg: alpha('#9e9e9e', 0.1)
        };
    }
  };

  // Handle opening role selection menu
  const handleOpenRoleMenu = (event) => {
    setRoleMenuAnchorEl(event.currentTarget);
  };

  // Handle closing role selection menu
  const handleCloseRoleMenu = () => {
    setRoleMenuAnchorEl(null);
  };
  
  // Handle status filter changes
  const handleStatusFilterChange = (filter) => {
    setStatusFilters(prev => ({
      ...prev,
      [filter]: !prev[filter]
    }));
  };
  
  // Handle opening filter menu
  const handleOpenFilterMenu = (event) => {
    setFilterMenuAnchorEl(event.currentTarget);
  };
  
  // Handle closing filter menu
  const handleCloseFilterMenu = () => {
    setFilterMenuAnchorEl(null);
  };
  
  // Export filtered users to CSV
  const exportToCSV = () => {
    if (finalFilteredUsers.length === 0) {
      setSnackbar({
        open: true,
        message: 'No users to export',
        severity: 'warning'
      });
      return;
    }
    
    try {
      // Prepare headers
      const headers = ['Name', 'Email', 'Role Owner', 'Total Roles', 'Reviewed Roles', 'Rejected Roles', 'Completion %', 'Roles List'];
      
      // Prepare rows
      const rows = finalFilteredUsers.map(user => [
        user.name,
        user.email,
        user.primaryRoleOwner,
        user.totalRoles,
        user.reviewedRoles,
        user.rejectedRoles,
        user.completionPercent + '%',
        user.rolesList
      ]);
      
      // Combine headers and rows
      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      ].join('\n');
      
      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `user-access-review-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      setSnackbar({
        open: true,
        message: `Exported ${finalFilteredUsers.length} users successfully`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Error exporting users:', error);
      setSnackbar({
        open: true,
        message: `Failed to export users: ${error.message || 'Unknown error'}`,
        severity: 'error'
      });
    }
  };
  
  // Open monitoring dialog
  const handleOpenMonitoringDialog = () => {
    setOpenMonitoringDialog(true);
  };
  
  // Close monitoring dialog
  const handleCloseMonitoringDialog = () => {
    setOpenMonitoringDialog(false);
  };
  
  // Export rejected users monitoring data
  const exportRejectedUsers = () => {
    if (dashboardData.rejectedUsersMonitoring.length === 0) {
      setSnackbar({
        open: true,
        message: 'No rejected users to export',
        severity: 'warning'
      });
      return;
    }
    
    try {
      // Prepare headers
      const headers = ['Name', 'Email', 'Department', 'App Record Status', 'Rejected Roles', 'Last Updated'];
      
      // Prepare rows
      const rows = dashboardData.rejectedUsersMonitoring.map(user => [
        user.name,
        user.email,
        user.department,
        user.appRecordStatus,
        user.rejectedRoleDetails.map(r => r.roleName).join('; '),
        user.lastUpdated
      ]);
      
      // Combine headers and rows
      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      ].join('\n');
      
      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `rejected-users-still-active-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      setSnackbar({
        open: true,
        message: `Exported ${dashboardData.rejectedUsersMonitoring.length} rejected users successfully`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Error exporting rejected users:', error);
      setSnackbar({
        open: true,
        message: `Failed to export rejected users: ${error.message || 'Unknown error'}`,
        severity: 'error'
      });
    }
  };

  // Toggle role owners view mode
  const toggleRoleOwnersViewMode = () => {
    setRoleOwnersViewMode(prev => {
      const newMode = prev === 'card' ? 'list' : 'card';
      // Save to localStorage
      localStorage.setItem('roleOwnersViewMode', newMode);
      return newMode;
    });
  };

  // Update handler for direct mode setting
  const handleSetViewMode = (mode) => {
    // Save to localStorage
    localStorage.setItem('roleOwnersViewMode', mode);
    setRoleOwnersViewMode(mode);
  };

  return (
    <Box sx={{ p: 3, bgcolor: '#f8fafc' }}>
      <StyledPaper elevation={0} sx={{ mb: 3, bgcolor: '#fff' }}>
        {/* Summary Stats Cards - Enhanced Look */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {/* Existing summary cards */}
          <Grid item xs={12} sm={6} md={3}>
            <SummaryCard 
              title="Total Roles"
              value={dashboardData.stats.totalRoles}
              icon={<Group />}
              color="#3b82f6" 
              bgColor={alpha("#3b82f6", 0.05)}
              description={`${dashboardData.stats.approvedRoles} approved, ${dashboardData.stats.rejectedRoles} rejected`}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <SummaryCard 
              title="Pending Review"
              value={dashboardData.stats.pendingRoles}
              icon={<Pending />}
              color="#f59e0b"
              bgColor={alpha("#f59e0b", 0.05)}
              description={`${dashboardData.stats.totalRoles - dashboardData.stats.pendingRoles} roles completed`}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <SummaryCard 
              title="Users Involved"
              value={dashboardData.stats.totalUsers}
              icon={<Person />}
              color="#10b981"
              bgColor={alpha("#10b981", 0.05)}
              description="Across all roles"
            />
          </Grid>
          {/* Add a new card for rejected users still active */}
          <Grid item xs={12} sm={6} md={3}>
            <SummaryCard 
              title="Rejected But Active"
              value={dashboardData.rejectedUsersMonitoring.length}
              icon={<Warning />}
              color="#ef4444"
              bgColor={alpha("#ef4444", 0.05)}
              description={
                <Button 
                  size="small" 
                  color="error" 
                  variant="text" 
                  onClick={handleOpenMonitoringDialog}
                  sx={{ p: 0, textTransform: 'none', fontSize: '0.7rem' }}
                >
                  {dashboardData.rejectedUsersMonitoring.length > 0 ? "View anomalies" : "No issues found"}
                </Button>
              }
            />
          </Grid>
        </Grid>

        {/* Three Main Sections */}
        <Grid container spacing={3}>
          {/* Roles Summary Section */}
          <Grid item xs={12} lg={4}>
            <StyledPaper 
              elevation={0} 
              sx={{ 
                height: '100%', 
                display: 'flex',
                flexDirection: 'column',
                border: '1px solid', 
                borderColor: alpha('#000', 0.08),
                bgcolor: '#fff'
              }}
            >
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#334155' }}>
                Roles Status
              </Typography>
              
              <Box sx={{ height: 200, mb: 3, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                {dashboardData.roleStatusData.length > 0 && dashboardData.stats.totalRoles > 0 ? (
                  <PieChart
                    series={[
                      {
                        data: dashboardData.roleStatusData,
                        innerRadius: 65,
                        outerRadius: 85,
                        paddingAngle: 3,
                        cornerRadius: 5,
                        startAngle: -90,
                        endAngle: 270,
                        highlightScope: { faded: 'global', highlighted: 'item' },
                        faded: { innerRadius: 55, additionalRadius: -5, color: 'gray' },
                      },
                    ]}
                    height={180}
                    margin={{ top: 10, bottom: 10, left: 10, right: 10 }}
                    slotProps={{
                      legend: { hidden: true },
                    }}
                  />
                ) : (
                  <Typography variant="body2" color="textSecondary">No role data available</Typography>
                )}
              </Box>
              
              <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 'medium', color: '#334155' }}>
                Pending Review ({dashboardData.pendingRoles.length})
              </Typography>
              
              {dashboardData.pendingRoles.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <TextField
                    size="small"
                    variant="outlined"
                    placeholder="Search roles..."
                    value={roleSearchQuery}
                    onChange={handleRoleSearchChange}
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search fontSize="small" sx={{ color: 'text.secondary' }} />
                        </InputAdornment>
                      ),
                      endAdornment: roleSearchQuery && (
                        <InputAdornment position="end">
                          <Clear
                            fontSize="small" 
                            sx={{ 
                              color: 'text.secondary',
                              cursor: 'pointer',
                              '&:hover': { color: 'text.primary' }
                            }}
                            onClick={handleClearRoleSearch}
                          />
                        </InputAdornment>
                      ),
                      sx: { 
                        fontSize: '0.875rem',
                        borderRadius: 1.5,
                        bgcolor: alpha('#000', 0.02),
                        '&:hover': { bgcolor: alpha('#000', 0.04) }
                      }
                    }}
                  />
                </Box>
              )}
              
              <Box sx={{ flex: 1, overflow: 'auto', maxHeight: 350, pr: 1, '&::-webkit-scrollbar': { width: '6px' }, '&::-webkit-scrollbar-thumb': { bgcolor: alpha('#000', 0.2), borderRadius: '3px' } }}>
                {filteredPendingRoles.length > 0 ? filteredPendingRoles.map((role) => (
                  <Box 
                    key={role.id}
                    onClick={() => handleRoleClick(role.name)}
                    sx={{ 
                      p: 1.5, 
                      mb: 1.5, 
                      borderRadius: 1.5, 
                      bgcolor: selectedRoleFilter.includes(role.name) ? alpha('#3b82f6', 0.1) : alpha('#000', 0.03),
                      border: '1px solid', 
                      borderColor: selectedRoleFilter.includes(role.name) ? alpha('#3b82f6', 0.3) : alpha('#000', 0.05),
                      cursor: 'pointer',
                      transition: 'background-color 0.2s, border-color 0.2s',
                      '&:hover': { 
                        bgcolor: selectedRoleFilter.includes(role.name) ? alpha('#3b82f6', 0.15) : alpha('#000', 0.06),
                        borderColor: selectedRoleFilter.includes(role.name) ? alpha('#3b82f6', 0.4) : alpha('#000', 0.1)
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'medium', color: 'text.primary' }} noWrap>
                        {role.name}
                      </Typography>
                      <Chip
                        size="small"
                        label={`${role.completionPercent}%`}
                        color={role.completionPercent > 0 ? 'warning' : 'default'}
                        sx={{ 
                          fontWeight: 500, 
                          height: 20, 
                          fontSize: '0.7rem',
                          bgcolor: alpha(role.completionPercent > 0 ? '#ff9800' : '#bdbdbd', 0.15),
                          color: role.completionPercent > 0 ? '#b26a00' : '#616161'
                        }}
                      />
                    </Box>
                    
                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1 }}>
                      Owner: {role.ownerName && role.ownerName !== 'Owner Not Found' ? 
                        (role.ownerName.includes('(') ? role.ownerName.split('(')[0].trim() : role.ownerName) 
                        : 'Unassigned'}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Tooltip title="Users assigned">
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Person fontSize="small" sx={{ fontSize: 14, mr: 0.5, color: 'text.secondary' }} />
                          <Typography variant="caption" color="textSecondary">
                            {role.totalUsers} user{role.totalUsers !== 1 ? 's' : ''}
                          </Typography>
                        </Box>
                      </Tooltip>
                      
                      {role.withSodViolation > 0 && (
                        <Tooltip title={`Contains ${role.withSodViolation} SOD violation(s)`}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Error fontSize="small" sx={{ fontSize: 14, mr: 0.5, color: '#ef4444' }} />
                            <Typography variant="caption" sx={{ color: '#ef4444', fontWeight: 500 }}>
                              {role.withSodViolation} conflict{role.withSodViolation !== 1 ? 's' : ''}
                            </Typography>
                          </Box>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
                )) : (
                  <Box sx={{ p: 2, textAlign: 'center', borderRadius: 1.5, bgcolor: alpha('#4caf50', 0.05), border: '1px solid', borderColor: alpha('#4caf50', 0.2) }}>
                     <CheckCircle sx={{ color: '#4caf50', fontSize: 30, mb: 1 }} />
                    <Typography variant="body2" sx={{ color: '#388e3c', fontWeight: 500 }}>
                      All roles reviewed!
                    </Typography>
                  </Box>
                )}
              </Box>
            </StyledPaper>
          </Grid>
          
          {/* Users Section */}
          <Grid item xs={12} lg={8}>
            <StyledPaper 
              elevation={0} 
              sx={{ 
                height: '100%', 
                display: 'flex',
                flexDirection: 'column',
                border: '1px solid', 
                borderColor: alpha('#000', 0.08),
                bgcolor: '#fff'
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#334155', mr: 2, flexShrink: 0 }}>
                    User Review Status
                  </Typography>
                  {selectedRoleFilter.length > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {selectedRoleFilter.length <= 2 ? (
                        // Show all chips directly if there are only 1-2 selected roles
                        selectedRoleFilter.map(role => (
                          <Chip
                            key={role}
                            label={role}
                            color="primary"
                            size="medium"
                            onDelete={() => handleRemoveRoleFilter(role)}
                            sx={{ 
                              mr: 1,
                              fontWeight: 500,
                              bgcolor: alpha('#3b82f6', 0.9),
                              '& .MuiChip-deleteIcon': {
                                color: 'white',
                                '&:hover': { color: 'white' }
                              }
                            }}
                          />
                        ))
                      ) : (
                        // Show first 2 roles and a count chip for the rest
                        <>
                          {selectedRoleFilter.slice(0, 2).map(role => (
                            <Chip
                              key={role}
                              label={role}
                              color="primary"
                              size="medium"
                              onDelete={() => handleRemoveRoleFilter(role)}
                              sx={{ 
                                mr: 1,
                                fontWeight: 500,
                                bgcolor: alpha('#3b82f6', 0.9),
                                '& .MuiChip-deleteIcon': {
                                  color: 'white',
                                  '&:hover': { color: 'white' }
                                }
                              }}
                            />
                          ))}
                          <Chip
                            label={`+${selectedRoleFilter.length - 2}`}
                            color="primary"
                            size="medium"
                            onClick={handleOpenRoleMenu}
                            sx={{ 
                              fontWeight: 500,
                              bgcolor: alpha('#3b82f6', 0.7),
                              cursor: 'pointer',
                              '&:hover': {
                                bgcolor: alpha('#3b82f6', 0.8),
                              }
                            }}
                          />
                        </>
                      )}
                      
                      {/* Menu button to clear all filters or manage them */}
                      <IconButton
                        size="small"
                        sx={{ ml: 1, color: alpha('#3b82f6', 0.9) }}
                        onClick={handleOpenRoleMenu}
                      >
                        <FilterListOff fontSize="small" />
                      </IconButton>
                      
                      {/* Role selection menu */}
                      <Menu
                        anchorEl={roleMenuAnchorEl}
                        open={Boolean(roleMenuAnchorEl)}
                        onClose={handleCloseRoleMenu}
                        PaperProps={{
                          elevation: 3,
                          sx: { 
                            mt: 1.5, 
                            width: 290,
                            maxHeight: 350,
                            borderRadius: 2,
                            '& .MuiList-root': { 
                              py: 1 
                            }
                          }
                        }}
                      >
                        <Box sx={{ px: 2, py: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${alpha('#000', 0.08)}` }}>
                          <Typography variant="subtitle2" fontWeight={600}>
                            Selected Roles ({selectedRoleFilter.length})
                          </Typography>
                          <Button 
                            size="small" 
                            color="primary" 
                            onClick={() => {
                              clearRoleFilter();
                              handleCloseRoleMenu();
                            }}
                            sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                          >
                            Clear All
                          </Button>
                        </Box>
                        {selectedRoleFilter.map(role => (
                          <MenuItem key={role} dense sx={{ py: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                              <Typography variant="body2" noWrap sx={{ maxWidth: 220 }}>
                                {role}
                              </Typography>
                              <IconButton 
                                size="small" 
                                edge="end"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRemoveRoleFilter(role);
                                }}
                                sx={{ ml: 1, color: 'text.secondary' }}
                              >
                                <Clear fontSize="small" />
                              </IconButton>
                            </Box>
                          </MenuItem>
                        ))}
                      </Menu>
                    </Box>
                  )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  {/* Status filter button */}
                  <Button
                    size="small"
                    variant="outlined"
                    color="primary"
                    startIcon={<FilterList />}
                    onClick={handleOpenFilterMenu}
                    sx={{ 
                      textTransform: 'none',
                      borderRadius: 2,
                      borderColor: alpha('#3b82f6', 0.5),
                      bgcolor: (!statusFilters.approved || !statusFilters.rejected || !statusFilters.pending) ? 
                        alpha('#3b82f6', 0.1) : 'transparent'
                    }}
                  >
                    Status
                  </Button>
                  
                  {/* Export button */}
                  <Button
                    size="small"
                    variant="outlined"
                    color="primary"
                    startIcon={<Download />}
                    onClick={exportToCSV}
                    sx={{ 
                      textTransform: 'none',
                      borderRadius: 2,
                      borderColor: alpha('#3b82f6', 0.5)
                    }}
                  >
                    Export
                  </Button>
                  
                  {/* Search field */}
                  <TextField
                    size="small"
                    variant="outlined"
                    placeholder="Search users..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search fontSize="small" sx={{ color: 'text.secondary' }} />
                        </InputAdornment>
                      ),
                      endAdornment: searchQuery && (
                        <InputAdornment position="end">
                          <Clear
                            fontSize="small" 
                            sx={{ 
                              color: 'text.secondary',
                              cursor: 'pointer',
                              '&:hover': { color: 'text.primary' }
                            }}
                            onClick={handleClearSearch}
                          />
                        </InputAdornment>
                      ),
                      sx: { 
                        fontSize: '0.875rem',
                        borderRadius: 1.5,
                        bgcolor: alpha('#000', 0.02),
                        '&:hover': { bgcolor: alpha('#000', 0.04) }
                      }
                    }}
                    sx={{ width: 220 }}
                  />
                  
                  {selectedRoleFilter.length > 0 && (
                    <Button 
                      size="small"
                      onClick={clearRoleFilter}
                      startIcon={<FilterListOff fontSize="small"/>}
                      sx={{ textTransform: 'none' }}
                    >
                      Clear All Filters
                    </Button>
                  )}
                </Box>
              </Box>
              
              {/* Status filter menu */}
              <Menu
                anchorEl={filterMenuAnchorEl}
                open={Boolean(filterMenuAnchorEl)}
                onClose={handleCloseFilterMenu}
                PaperProps={{
                  elevation: 3,
                  sx: { 
                    mt: 1, 
                    minWidth: 180,
                    borderRadius: 2,
                    p: 1
                  }
                }}
              >
                <Typography variant="subtitle2" sx={{ px: 2, py: 1, fontWeight: 600 }}>
                  Filter by status
                </Typography>
                <MenuItem dense onClick={() => handleStatusFilterChange('approved')}>
                  <ListItemIcon>
                    <Checkbox 
                      size="small" 
                      edge="start" 
                      checked={statusFilters.approved} 
                      sx={{ color: '#4caf50', '&.Mui-checked': { color: '#4caf50' } }} 
                    />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Approved" 
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                  <CheckCircle fontSize="small" sx={{ ml: 1, color: '#4caf50' }} />
                </MenuItem>
                <MenuItem dense onClick={() => handleStatusFilterChange('rejected')}>
                  <ListItemIcon>
                    <Checkbox 
                      size="small" 
                      edge="start" 
                      checked={statusFilters.rejected} 
                      sx={{ color: '#f44336', '&.Mui-checked': { color: '#f44336' } }} 
                    />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Rejected" 
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                  <ErrorOutline fontSize="small" sx={{ ml: 1, color: '#f44336' }} />
                </MenuItem>
                <MenuItem dense onClick={() => handleStatusFilterChange('pending')}>
                  <ListItemIcon>
                    <Checkbox 
                      size="small" 
                      edge="start" 
                      checked={statusFilters.pending} 
                      sx={{ color: '#ff9800', '&.Mui-checked': { color: '#ff9800' } }} 
                    />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Pending" 
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                  <Pending fontSize="small" sx={{ ml: 1, color: '#ff9800' }} />
                </MenuItem>
                <Box sx={{ 
                  borderTop: `1px solid ${alpha('#000', 0.08)}`, 
                  mt: 1, 
                  pt: 1,
                  px: 1,
                  display: 'flex',
                  justifyContent: 'flex-end'
                }}>
                  <Button 
                    size="small" 
                    onClick={handleCloseFilterMenu}
                    sx={{ textTransform: 'none' }}
                  >
                    Apply
                  </Button>
                </Box>
              </Menu>
              
              {/* Display counts of filtered users */}
              {((!statusFilters.approved || !statusFilters.rejected || !statusFilters.pending) ||
               selectedRoleFilter.length > 0 || searchQuery) && (
                <Box sx={{ mb: 2, mt: -1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Showing {finalFilteredUsers.length} of {dashboardData.allUsers.length} users
                    {!statusFilters.approved && !statusFilters.rejected && !statusFilters.pending ? 
                      ' (no status filters selected)' :
                      (!statusFilters.approved || !statusFilters.rejected || !statusFilters.pending) ?
                        ` (filtered by status: ${statusFilters.approved ? 'Approved ' : ''}${statusFilters.rejected ? 'Rejected ' : ''}${statusFilters.pending ? 'Pending' : ''})` : ''}
                  </Typography>
                </Box>
              )}
              
              {/* User data grid - existing code */}
              <Box sx={{ height: 600, width: '100%' }}>
                <DataGrid
                  rows={finalFilteredUsers}
                  columns={userColumns}
                  initialState={{
                      pagination: { paginationModel: { pageSize: 10 } },
                      sorting: {
                        sortModel: [{ field: 'completionPercent', sort: 'asc' }],
                      },
                      columns: {
                        columnVisibilityModel: {
                          // Hide rolesList by default for cleaner initial view
                          rolesList: false
                        }
                      }
                  }}
                  pageSizeOptions={[5, 10, 25, 50]}
                  disableRowSelectionOnClick
                  rowHeight={55}
                  autoHeight
                  density="compact"
                  getRowHeight={() => 'auto'}
                  getEstimatedRowHeight={() => 55}
                  sx={{
                    border: 'none',
                    fontSize: '0.8rem',
                    '& .MuiDataGrid-main': {
                      // Target the main container to ensure proper layout
                      overflow: 'visible'
                    },
                    '& .MuiDataGrid-columnHeaders': {
                      bgcolor: alpha('#000', 0.02),
                      borderBottom: `1px solid ${alpha('#000', 0.08)}`,
                      '& .MuiDataGrid-columnHeaderTitle': {
                        fontWeight: 600,
                        color: '#475569',
                        fontSize: '0.8rem'
                      }
                    },
                    '& .MuiDataGrid-cell': {
                      borderBottom: `1px solid ${alpha('#000', 0.05)}`,
                      padding: '8px 16px',
                      '&:focus': {
                        outline: 'none' // Remove cell focus outline
                      },
                      '&:focus-within': {
                        outline: 'none' // Remove focus outline
                      },
                      '& .MuiTypography-body2': {
                        fontSize: '0.8rem'
                      },
                      '& .MuiTypography-caption': {
                        fontSize: '0.7rem'
                      }
                    },
                    '& .MuiDataGrid-row': {
                      '&:hover': {
                        backgroundColor: alpha("#3b82f6", 0.04)
                      }
                    },
                    '& .MuiDataGrid-footerContainer': {
                      borderTop: `1px solid ${alpha('#000', 0.08)}`,
                      '& .MuiTablePagination-root': {
                        fontSize: '0.8rem'
                      }
                    },
                    '& .MuiDataGrid-virtualScroller': {
                        '&::-webkit-scrollbar': { width: '6px' },
                        '&::-webkit-scrollbar-thumb': { bgcolor: alpha('#000', 0.2), borderRadius: '3px' }
                    }
                  }}
                />
              </Box>
            </StyledPaper>
          </Grid>
          
          {/* Role Owners Section */}
          <Grid item xs={12}>
            <StyledPaper 
              elevation={0} 
              sx={{ 
                border: '1px solid', 
                borderColor: alpha('#000', 0.08),
                bgcolor: '#fff'
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#334155' }}>
                  Role Owners Pending Action ({dashboardData.pendingRoleOwners.length})
                </Typography>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  {/* View mode toggle */}
                  <ButtonGroup size="small" sx={{ borderRadius: 1.5, overflow: 'hidden' }}>
                    <Button
                      variant={roleOwnersViewMode === 'card' ? 'contained' : 'outlined'}
                      color="primary"
                      onClick={() => handleSetViewMode('card')}
                      sx={{ 
                        textTransform: 'none',
                        fontSize: '0.75rem',
                        px: 1.5
                      }}
                      startIcon={<GridView fontSize="small" />}
                    >
                      Cards
                    </Button>
                    <Button
                      variant={roleOwnersViewMode === 'list' ? 'contained' : 'outlined'}
                      color="primary"
                      onClick={() => handleSetViewMode('list')}
                      sx={{ 
                        textTransform: 'none',
                        fontSize: '0.75rem',
                        px: 1.5
                      }}
                      startIcon={<ViewList fontSize="small" />}
                    >
                      List
                    </Button>
                  </ButtonGroup>
                  
                  {dashboardData.pendingRoleOwners.length > 0 && (
                    <Button
                      variant="contained"
                      startIcon={<Send />}
                      disabled={isLoading}
                      onClick={confirmBulkReminders}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 500,
                        bgcolor: alpha('#3b82f6', 0.9),
                        '&:hover': {
                          bgcolor: '#3b82f6'
                        }
                      }}
                    >
                      {isLoading ? (
                        <>
                          <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                          Sending...
                        </>
                      ) : (
                        'Send Bulk Reminders'
                      )}
                    </Button>
                  )}
                </Box>
              </Box>
              
              {dashboardData.pendingRoleOwners.length > 0 ? (
                roleOwnersViewMode === 'card' ? (
                  <Grid container spacing={2.5}>
                    {dashboardData.pendingRoleOwners.map((owner) => (
                      <Grid item xs={12} sm={6} md={4} key={owner.id}>
                        <Card 
                          variant="outlined" 
                          sx={{ 
                            height: '100%', 
                            borderColor: alpha('#000', 0.1),
                            borderRadius: 1.5,
                            transition: 'all 0.2s ease',
                            '&:hover': { 
                              boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
                              borderColor: alpha('#3b82f6', 0.4)
                            }
                          }}
                        >
                          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                              <Avatar sx={{ 
                                bgcolor: alpha(owner.completionPercent > 50 ? '#ff9800' : '#f44336', 0.15),
                                color: owner.completionPercent > 50 ? '#b26a00' : '#aa2e25',
                                width: 42, 
                                height: 42,
                                mr: 1.5,
                                fontSize: '1.1rem'
                              }}>
                                {owner.name?.[0]?.toUpperCase() || owner.email?.[0]?.toUpperCase() || 'O'}
                              </Avatar>
                              <Box sx={{ overflow: 'hidden' }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 500, color: 'text.primary' }} noWrap>
                                  {owner.name || owner.email}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap>
                                  {owner.email || 'No email available'}
                                </Typography>
                              </Box>
                            </Box>

                            <Box sx={{ mb: 2 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                <Typography variant="caption" color="text.secondary">Review Progress</Typography>
                                <Typography variant="caption" sx={{ fontWeight: 500, color: owner.completionPercent > 50 ? '#ff9800' : '#f44336' }}>
                                  {owner.completionPercent}%
                                </Typography>
                              </Box>
                              <LinearProgress 
                                variant="determinate" 
                                value={owner.completionPercent}
                                sx={{ 
                                  height: 6, 
                                  borderRadius: 3, 
                                  bgcolor: alpha(owner.completionPercent > 50 ? '#ff9800' : '#f44336', 0.2),
                                  '& .MuiLinearProgress-bar': {
                                    bgcolor: owner.completionPercent > 50 ? '#ff9800' : '#f44336'
                                  }
                                }} 
                              />
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                {owner.reviewedRolesCount} of {owner.totalRolesAssigned} roles reviewed
                              </Typography>
                            </Box>
                            
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                Responsible for Roles:
                              </Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                                {owner.rolesArray.slice(0, 4).map((role, i) => (
                                  <Chip
                                    key={i}
                                    label={role}
                                    size="small"
                                    variant="outlined"
                                    sx={{ fontSize: '0.7rem', borderRadius: '4px', bgcolor: alpha("#64748b", 0.08), borderColor: alpha("#64748b", 0.3) }}
                                  />
                                ))}
                                {owner.rolesArray.length > 4 && (
                                  <Chip
                                    label={`+${owner.rolesArray.length - 4}`}
                                    size="small"
                                    sx={{ fontSize: '0.7rem', borderRadius: '4px', bgcolor: alpha("#64748b", 0.08) }}
                                  />
                                )}
                              </Box>
                            </Box>
                            
                            <Button
                              variant="contained"
                              size="small"
                              color="primary"
                              fullWidth
                              disabled={isLoading}
                              onClick={() => handleSendReminder(owner)}
                              startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : <Send fontSize="small" />}
                              sx={{ 
                                textTransform: 'none', 
                                fontWeight: 500,
                                bgcolor: alpha('#3b82f6', 0.1),
                                color: '#3b82f6',
                                boxShadow: 'none',
                                '&:hover': {
                                  bgcolor: alpha('#3b82f6', 0.2),
                                  boxShadow: 'none'
                                },
                                '&.Mui-disabled': {
                                  bgcolor: alpha('#3b82f6', 0.05),
                                  color: alpha('#3b82f6', 0.5)
                                }
                              }}
                            >
                              {isLoading ? 'Sending...' : 'Send Reminder'}
                            </Button>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  // List view implementation
                  <TableContainer sx={{ maxHeight: 600, overflowY: 'auto' }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600, bgcolor: alpha('#000', 0.02) }}>Role Owner</TableCell>
                          <TableCell sx={{ fontWeight: 600, bgcolor: alpha('#000', 0.02) }}>Email</TableCell>
                          <TableCell sx={{ fontWeight: 600, bgcolor: alpha('#000', 0.02) }}>Progress</TableCell>
                          <TableCell sx={{ fontWeight: 600, bgcolor: alpha('#000', 0.02) }}>Roles</TableCell>
                          <TableCell sx={{ fontWeight: 600, bgcolor: alpha('#000', 0.02) }}>Status</TableCell>
                          <TableCell sx={{ fontWeight: 600, bgcolor: alpha('#000', 0.02) }}>Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {dashboardData.pendingRoleOwners.map((owner) => (
                          <TableRow key={owner.id} hover>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Avatar sx={{ 
                                  bgcolor: alpha(owner.completionPercent > 50 ? '#ff9800' : '#f44336', 0.15),
                                  color: owner.completionPercent > 50 ? '#b26a00' : '#aa2e25',
                                  width: 32, 
                                  height: 32,
                                  fontSize: '0.875rem'
                                }}>
                                  {owner.name?.[0]?.toUpperCase() || owner.email?.[0]?.toUpperCase() || 'O'}
                                </Avatar>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {owner.name || owner.email || 'Unknown Owner'}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>{owner.email || 'No email available'}</TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: 180 }}>
                                <LinearProgress 
                                  variant="determinate" 
                                  value={owner.completionPercent}
                                  sx={{ 
                                    height: 5, 
                                    borderRadius: 3, 
                                    flexGrow: 1,
                                    bgcolor: alpha(owner.completionPercent > 50 ? '#ff9800' : '#f44336', 0.2),
                                    '& .MuiLinearProgress-bar': {
                                      bgcolor: owner.completionPercent > 50 ? '#ff9800' : '#f44336'
                                    }
                                  }} 
                                />
                                <Typography variant="caption" sx={{ fontWeight: 500, color: owner.completionPercent > 50 ? '#ff9800' : '#f44336' }}>
                                  {owner.completionPercent}%
                                </Typography>
                              </Box>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                {owner.reviewedRolesCount} of {owner.totalRolesAssigned} roles reviewed
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxWidth: 300 }}>
                                {owner.rolesArray.slice(0, 3).map((role, i) => (
                                  <Chip
                                    key={i}
                                    label={role}
                                    size="small"
                                    variant="outlined"
                                    sx={{ fontSize: '0.7rem', borderRadius: '4px', bgcolor: alpha("#64748b", 0.08), borderColor: alpha("#64748b", 0.3) }}
                                  />
                                ))}
                                {owner.rolesArray.length > 3 && (
                                  <Tooltip title={owner.rolesArray.slice(3).join(', ')}>
                                    <Chip
                                      label={`+${owner.rolesArray.length - 3}`}
                                      size="small"
                                      sx={{ fontSize: '0.7rem', borderRadius: '4px', bgcolor: alpha("#64748b", 0.08) }}
                                    />
                                  </Tooltip>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={owner.completionPercent > 50 ? "In Progress" : "Not Started"}
                                sx={{ 
                                  fontSize: '0.7rem',
                                  bgcolor: alpha(owner.completionPercent > 50 ? '#ff9800' : '#f44336', 0.1),
                                  color: owner.completionPercent > 50 ? '#b26a00' : '#aa2e25',
                                  fontWeight: 500
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Button
                                variant="outlined"
                                size="small"
                                color="primary"
                                disabled={isLoading}
                                onClick={() => handleSendReminder(owner)}
                                startIcon={isLoading ? <CircularProgress size={14} color="inherit" /> : <Send fontSize="small" />}
                                sx={{ 
                                  textTransform: 'none', 
                                  fontSize: '0.7rem',
                                  py: 0.5
                                }}
                              >
                                {isLoading ? 'Sending...' : 'Send Reminder'}
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )
              ) : (
                <Box sx={{ p: 3, textAlign: 'center', borderRadius: 2, bgcolor: alpha('#4caf50', 0.05), border: '1px solid', borderColor: alpha('#4caf50', 0.2) }}>
                  <AssignmentTurnedIn sx={{ fontSize: 40, color: '#4caf50', mb: 1 }} />
                  <Typography variant="body1" sx={{ fontWeight: 'medium', color: '#388e3c' }}>
                    All role owners have completed their reviews.
                  </Typography>
                </Box>
              )}
            </StyledPaper>
          </Grid>
        </Grid>
      </StyledPaper>
      
      {/* Add Monitoring Dialog for Rejected but Active Users */}
      <Dialog
        open={openMonitoringDialog}
        onClose={handleCloseMonitoringDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          elevation: 3,
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle 
          sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            bgcolor: alpha('#ef4444', 0.05),
            color: '#ef4444',
            borderBottom: `1px solid ${alpha('#ef4444', 0.2)}`
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Warning />
            <Typography variant="h6">
              Rejected Users Still Active in System
            </Typography>
          </Box>
          <IconButton onClick={handleCloseMonitoringDialog} size="small">
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {dashboardData.rejectedUsersMonitoring.length > 0 ? (
            <TableContainer sx={{ maxHeight: 500 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, bgcolor: alpha('#000', 0.02) }}>User</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: alpha('#000', 0.02) }}>Email</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: alpha('#000', 0.02) }}>Department</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: alpha('#000', 0.02) }}>Rejected Roles</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: alpha('#000', 0.02) }}>Current Status</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: alpha('#000', 0.02) }}>Last Updated</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dashboardData.rejectedUsersMonitoring.map((user, idx) => (
                    <TableRow key={idx} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar 
                            sx={{ 
                              width: 28, 
                              height: 28, 
                              bgcolor: alpha('#ef4444', 0.1),
                              color: '#ef4444',
                              fontSize: '0.775rem'
                            }}
                          >
                            {user.firstName?.[0] || user.email?.[0]?.toUpperCase() || 'U'}
                          </Avatar>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {user.name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>{user.department}</TableCell>
                      <TableCell>
                        <Box>
                          {user.rejectedRoleDetails.map((role, i) => (
                            <Tooltip 
                              key={i}
                              title={`Reviewed by ${role.ownerName} on ${new Date(role.reviewDate).toLocaleDateString()}`}
                            >
                              <Chip
                                label={role.roleName}
                                size="small"
                                sx={{ 
                                  fontSize: '0.7rem',
                                  mr: 0.5,
                                  mb: 0.5,
                                  bgcolor: alpha('#ef4444', 0.1),
                                  color: '#ef4444',
                                  borderColor: alpha('#ef4444', 0.3)
                                }}
                                variant="outlined"
                              />
                            </Tooltip>
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={user.appRecordStatus === 'Y' ? 'Active' : user.appRecordStatus}
                          size="small"
                          color="error"
                          sx={{ 
                            fontSize: '0.7rem',
                            fontWeight: 500
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        {user.lastUpdated !== 'Unknown' 
                          ? new Date(user.lastUpdated).toLocaleDateString() 
                          : 'Unknown'
                        }
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <CheckCircle sx={{ fontSize: 48, color: '#4caf50', mb: 2 }} />
              <Typography variant="h6" sx={{ mb: 1, color: '#4caf50' }}>
                No Issues Found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                All rejected users have been properly deactivated in the system.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: `1px solid ${alpha('#000', 0.08)}` }}>
          {dashboardData.rejectedUsersMonitoring.length > 0 && (
            <Button
              variant="contained"
              color="error"
              startIcon={<Download />}
              onClick={exportRejectedUsers}
              sx={{ mr: 1, textTransform: 'none' }}
            >
              Export List
            </Button>
          )}
          <Button 
            onClick={handleCloseMonitoringDialog} 
            variant="outlined"
            color="inherit"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* User Details Dialog */}
      <Dialog 
        open={openUserDialog} 
        onClose={handleCloseUserDialog} 
        maxWidth="lg" 
        fullWidth
        fullScreen={fullScreen}
        PaperProps={{
          elevation: 5,
          sx: {
            borderRadius: 2,
            overflow: 'hidden'
          }
        }}
      >
        {selectedUser && (
          <>
            <DialogTitle 
              sx={{ 
                p: 0,
                position: 'relative',
                background: `linear-gradient(to right, ${alpha('#1976d2', 0.8)}, ${alpha('#1976d2', 0.6)})`,
                color: 'white',
                py: 3,
                px: 4
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar 
                  sx={{ 
                    width: 56, 
                    height: 56, 
                    bgcolor: '#fff',
                    color: '#1976d2',
                    border: '2px solid white',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                  }}
                >
                  {selectedUser.firstName?.[0] || selectedUser.email?.[0]?.toUpperCase() || 'U'}
                </Avatar>
                <Box>
                  <Typography variant="h5" component="div" sx={{ fontWeight: 600 }}>
                    {selectedUser.name}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.9 }}>
                    {selectedUser.email}
                  </Typography>
                </Box>
              </Box>
              <IconButton
                aria-label="close"
                onClick={handleCloseUserDialog}
                sx={{
                  position: 'absolute',
                  right: 16,
                  top: 16,
                  color: 'white',
                }}
              >
                <Close />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: 0 }}>
              <Grid container sx={{ p: 3 }}>
                <Grid item xs={12} md={4} sx={{ p: 2 }}>
                  <Paper elevation={0} sx={{ p: 2, height: '100%', border: `1px solid ${alpha('#000', 0.08)}` }}>
                    <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: '#334155', pb: 1, borderBottom: `1px solid ${alpha('#000', 0.08)}` }}>
                      User Summary
                    </Typography>
                    
                    <Stack spacing={2}>
                      <Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Review Progress
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <LinearProgress 
                            variant="determinate" 
                            value={selectedUser.completionPercent} 
                            sx={{ 
                              height: 5, 
                              borderRadius: 3, 
                              flexGrow: 1,
                              mr: 1.5,
                              bgcolor: alpha(selectedUser.completionPercent === 100 ? '#4caf50' : '#ff9800', 0.2),
                              '& .MuiLinearProgress-bar': {
                                bgcolor: selectedUser.completionPercent === 100 ? '#4caf50' : '#ff9800'
                              }
                            }} 
                          />
                          <Typography variant="body2" sx={{ 
                            fontWeight: 600, 
                            color: selectedUser.completionPercent === 100 ? '#4caf50' : '#ff9800', 
                            minWidth: 32, 
                            textAlign: 'right', 
                            fontSize: '0.75rem' 
                          }}>
                            {selectedUser.completionPercent}%
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {selectedUser.reviewedRoles + selectedUser.rejectedRoles} of {selectedUser.totalRoles} roles reviewed
                        </Typography>
                      </Box>

                      <Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Role Count
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Group sx={{ color: alpha('#1976d2', 0.8), fontSize: 20 }} />
                          <Typography variant="h6" sx={{ fontWeight: 600, color: '#334155' }}>
                            {selectedUser.totalRoles}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            assigned roles
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Role Owners
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {selectedUser.roleOwners?.length > 0 ? (
                            selectedUser.roleOwners.map((owner, idx) => (
                              <Chip 
                                key={idx}
                                label={owner} 
                                size="small" 
                                variant="outlined"
                                sx={{ 
                                  fontSize: '0.75rem',
                                  borderColor: alpha('#1976d2', 0.3),
                                  color: '#1976d2'
                                }} 
                              />
                            ))
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              No role owners assigned
                            </Typography>
                          )}
                        </Box>
                      </Box>
                      
                      {selectedUser.roles?.some(role => role.withSodViolation) && (
                        <Box sx={{ mt: 2 }}>
                          <Badge 
                            badgeContent={selectedUser.roles.filter(role => role.withSodViolation).length} 
                            color="error"
                            sx={{ '& .MuiBadge-badge': { fontSize: '0.7rem', height: 18, minWidth: 18 } }}
                          >
                            <Chip
                              icon={<ErrorOutline />} 
                              label="SOD Violations" 
                              color="error"
                              size="small"
                              variant="outlined"
                              sx={{ 
                                borderColor: alpha('#f44336', 0.5),
                                '& .MuiChip-label': { fontWeight: 500 }
                              }}
                            />
                          </Badge>
                        </Box>
                      )}
                    </Stack>
                  </Paper>
                </Grid>
                
                <Grid item xs={12} md={8} sx={{ p: 2 }}>
                  <Paper elevation={0} sx={{ height: '100%', border: `1px solid ${alpha('#000', 0.08)}` }}>
                    <TableContainer sx={{ maxHeight: 400, overflowY: 'auto' }}>
                      <Table stickyHeader size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600, bgcolor: alpha('#000', 0.02) }}>Role Name</TableCell>
                            <TableCell sx={{ fontWeight: 600, bgcolor: alpha('#000', 0.02) }}>Role Owner</TableCell>
                            <TableCell sx={{ fontWeight: 600, bgcolor: alpha('#000', 0.02) }}>Status</TableCell>
                            <TableCell sx={{ fontWeight: 600, bgcolor: alpha('#000', 0.02) }} align="center">SOD Violation</TableCell>
                            <TableCell sx={{ fontWeight: 600, bgcolor: alpha('#000', 0.02) }}>Review Date</TableCell>
                            <TableCell sx={{ fontWeight: 600, bgcolor: alpha('#000', 0.02) }}>Action</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {selectedUser.roles?.map((role, idx) => {
                            const statusProps = getRoleStatusProps(role.status);
                            return (
                              <TableRow key={idx} hover>
                                <TableCell sx={{ fontWeight: 500 }}>{role.roleName}</TableCell>
                                <TableCell>
                                  <Tooltip title={role.ownerEmail || ''}>
                                    <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                                      {role.ownerName || 'Unassigned'}
                                    </Typography>
                                  </Tooltip>
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    icon={statusProps.icon}
                                    label={statusProps.label}
                                    size="small"
                                    sx={{ 
                                      fontSize: '0.7rem',
                                      bgcolor: statusProps.bg,
                                      color: statusProps.color,
                                      border: 'none',
                                      fontWeight: 500
                                    }}
                                  />
                                </TableCell>
                                <TableCell align="center">
                                  {role.withSodViolation ? (
                                    <Tooltip title="Has SOD Violation">
                                      <ErrorOutline sx={{ color: '#f44336', fontSize: 18 }} />
                                    </Tooltip>
                                  ) : (
                                    <Tooltip title="No SOD Violation">
                                      <CheckCircleOutline sx={{ color: '#4caf50', fontSize: 18 }} />
                                    </Tooltip>
                                  )}
                                </TableCell>
                                <TableCell>
                                  {role.reviewDate ? new Date(role.reviewDate).toLocaleDateString() : 'Not reviewed yet'}
                                </TableCell>
                                <TableCell>
                                  {role.status !== 'Reviewed' && role.status !== 'Approved' && (
                                    <Button
                                      variant="outlined"
                                      size="small"
                                      color="primary"
                                      disabled={isLoading || !role.ownerName || role.ownerName === 'Owner Not Found'}
                                      onClick={() => handleSendRoleReminder(role)}
                                      startIcon={isLoading ? <CircularProgress size={14} color="inherit" /> : <Send fontSize="small" />}
                                      sx={{ 
                                        textTransform: 'none', 
                                        fontSize: '0.7rem',
                                        py: 0.25,
                                        borderRadius: 1
                                      }}
                                    >
                                      {isLoading ? 'Sending...' : 'Remind'}
                                    </Button>
                                  )}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    
                    {(!selectedUser.roles || selectedUser.roles.length === 0) && (
                      <Box sx={{ p: 3, textAlign: 'center' }}>
                        <InfoOutlined sx={{ color: alpha('#000', 0.3), fontSize: 48, mb: 1 }} />
                        <Typography variant="body1" color="text.secondary">
                          No roles assigned to this user
                        </Typography>
                      </Box>
                    )}
                  </Paper>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${alpha('#000', 0.08)}` }}>
              <Button 
                onClick={handleCloseUserDialog} 
                variant="outlined"
                color="inherit"
                sx={{ borderRadius: 2 }}
              >
                Close
              </Button>
              {selectedUser && selectedUser.roles && selectedUser.roles.some(role => 
                role.status !== 'Reviewed' && role.status !== 'Approved' && role.ownerName && role.ownerName !== 'Owner Not Found'
              ) && (
                <Button 
                  variant="contained"
                  color="primary"
                  disabled={isLoading}
                  startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <Send />}
                  onClick={handleSendUserReminders}
                  sx={{ 
                    borderRadius: 2, 
                    textTransform: 'none',
                    fontWeight: 500,
                    mr: 1
                  }}
                >
                  {isLoading ? 'Sending...' : 'Send Reminders'}
                </Button>
              )}
             
            </DialogActions>
          </>
        )}
      </Dialog>
      
      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={handleCloseConfirmDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          elevation: 3,
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: alpha(confirmDialog.title?.includes('Success') ? '#4caf50' : 
                         confirmDialog.title?.includes('Failed') ? '#f44336' : 
                         confirmDialog.title?.includes('Partial') ? '#ff9800' : 
                         '#3b82f6', 0.1), 
          color: confirmDialog.title?.includes('Success') ? '#4caf50' : 
                 confirmDialog.title?.includes('Failed') ? '#f44336' : 
                 confirmDialog.title?.includes('Partial') ? '#ff9800' : 
                 '#3b82f6', 
          py: 2 
        }}>
          {confirmDialog.title}
        </DialogTitle>
        <DialogContent sx={{ py: 3, mt: 1 }}>
          {confirmDialog.message?.includes('Results of reminder operation:') || 
            confirmDialog.message?.includes('Reminders sent to the following role owners:') ? (
            <Box>
              {confirmDialog.message.split('\n\n').map((paragraph, i) => (
                <Typography key={i} variant="body1" sx={{ mb: paragraph.startsWith('Results') || paragraph.startsWith('Reminders') ? 2 : 0 }}>
                  {paragraph.startsWith('Results') || paragraph.startsWith('Reminders') ? paragraph : (
                    <Box component="div" sx={{ 
                      backgroundColor: alpha('#f5f5f5', 0.6),
                      borderRadius: 1,
                      p: 2,
                      maxHeight: '200px',
                      overflowY: 'auto',
                      '& ul': { pl: 2, mt: 1 },
                      '& li': { mb: 1 }
                    }}>
                      <ul>
                        {paragraph.split('\n').filter(line => line.trim()).map((line, j) => (
                          <li key={j}>
                            <Typography variant="body2" component="span">
                              {line.replace('• ', '')}
                            </Typography>
                          </li>
                        ))}
                      </ul>
                    </Box>
                  )}
                </Typography>
              ))}
            </Box>
          ) : (
            <Typography variant="body1">
              {confirmDialog.message}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {confirmDialog.title === 'Send Bulk Reminders' || 
           confirmDialog.title === 'Send Reminder' ||
           confirmDialog.title === 'Send Reminders for User' ? (
            <>
              <Button 
                onClick={handleCloseConfirmDialog} 
                color="inherit"
                variant="outlined"
                sx={{ borderRadius: 2 }}
              >
                Cancel
              </Button>
              <Button 
                onClick={confirmDialog.onConfirm}
                variant="contained"
                color="primary"
                sx={{ 
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 500
                }}
              >
                Send Reminders
              </Button>
            </>
          ) : (
            <Button 
              onClick={confirmDialog.onConfirm || handleCloseConfirmDialog}
              variant="contained"
              color="primary"
              sx={{ 
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500
              }}
            >
              Close
            </Button>
          )}
        </DialogActions>
      </Dialog>
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity} 
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// Enhanced Summary Card Component
const SummaryCard = ({ title, value, icon, color, bgColor, description }) => {
  return (
    <StyledPaper 
      elevation={0} 
      sx={{
        position: 'relative', 
        overflow: 'hidden', 
        p: 2.5, 
        bgcolor: '#fff',
        border: '1px solid', 
        borderColor: alpha('#000', 0.08),
        transition: 'all 0.2s ease',
        '&:hover': {
          borderColor: alpha(color, 0.4),
          boxShadow: `0 6px 16px ${alpha(color, 0.1)}`
        }
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
            {title}
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'text.primary', mb: 1 }}>
            {value}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {description}
          </Typography>
        </Box>
        <Avatar sx={{ 
          bgcolor: bgColor || alpha(color, 0.1),
          color: color,
          width: 48,
          height: 48,
          mt: 0.5
        }}>
          {React.cloneElement(icon, { fontSize: 'medium' })}
        </Avatar>
      </Box>
      {/* Subtle bottom border accent */}
      <Box 
        sx={{
          position: 'absolute', 
          bottom: 0, 
          left: 0, 
          right: 0, 
          height: '4px', 
          bgcolor: color,
          opacity: 0.8
        }}
      />
    </StyledPaper>
  );
};

export default UARReviewDashboard; 